import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import {
    OrbitControls,
    TransformControls,
    Environment,
    ContactShadows,
    Html,
    Line,
    Box
} from '@react-three/drei';
import { Spinner, Text, tokens, ToggleButton } from '@fluentui/react-components';
import * as THREE from 'three';
import { trackEvent } from '../utils/analytics';

import { iconMap } from '../theme/icons';

import silhouetteImg from '../assets/img/human_silhouette.png';
import InnerSelectingDialog from './dialogs/InnerSelectingDialog';
import OuterSelectingDialog from './dialogs/OuterSelectingDialog';
import Wall from './components/Wall';
import FurnitureGroup from './components/FurnitureGroup'; // Importuojame FurnitureGroup
import UnifiedBase from './components/UnifiedBase';
import Measurements from './components/Measurements';
import ArrowHead from './components/ArrowHead';
import CaptureFrameNotifier from './utils/CaptureFrameNotifier';
import { DepthHoverContext } from './utils/edgeHover.jsx';

const VisibilityIcon = iconMap.visibility;
const VisibilityOffIcon = iconMap.visibility_off;

// Mirrors InnerSelectingDialog.jsx's SHELF_TYPES/SHELF_TOP_GAP_DEFAULT_CM/getGapAboveCm —
// just enough to compute the vacated-space compensation for the keyboard-Delete single-item
// path below, which (unlike the dialog) has no local "items" state to read the current
// topGapCm from, only the saved array itself.
const INNER_SHELF_TYPES = new Set([10, 11]);
const INNER_SHELF_TOP_GAP_DEFAULT_CM = { 10: 27, 11: 27 };
const INNER_DRAWER_GAP_CM = 2.5;
const getInnerItemGapAboveCm = (item, idx) => {
    if (INNER_SHELF_TYPES.has(item.type)) return item.topGap ?? INNER_SHELF_TOP_GAP_DEFAULT_CM[item.type];
    if (idx !== 0) return INNER_DRAWER_GAP_CM;
    return item.topGapCm ?? 0;
};

// Patalpos "priekio" gylis (cm) — PRIE ŠITO fiksuojama kamera (žr. CameraAligner zBias),
// prie jo turi sekti VISKAS kita, kas vaizduojama kaip "prie priekinio krašto": matmenų
// rodyklės (measurementsZ), žmogaus siluetas (HumanSilhouette). TIK realiai matomos
// pusės gylis turi reikšmės — Wall.jsx pačios geometrijos (grindys/lubos/sienos) tą pačią
// taisyklę jau taikė teisingai, bet PRIEŠ šitą bendrą funkciją trys atskiros, NESUSIETOS
// kopijos (CameraAligner, HumanSilhouette, measurementsZ) šios logikos realiai sutapo tik
// DVI iš trijų — measurementsZ liko sena, TIK wallDepth (kairė) versija, kol vartotojas
// aptiko asimetrinėje Nišoje (kairė seklesnė, dešinė gilesnė) matmenų linijas/ženkliukus
// "įlendančius" į grindis — jos stovėjo prie SEKLESNĖS (kairės) sienos gylio, o grindys
// (teisingai) tęsėsi iki GILESNĖS (dešinės). Viena bendra funkcija = nebegali vėl išsiskirti.
const getEffWallDepth = (roomShape, wallDepth, wallDepthRight) => {
    const showLeftWall = roomShape === 1 || roomShape === 2;
    const showRightWall = roomShape === 1 || roomShape === 3;
    return (showLeftWall && showRightWall)
        ? Math.max(wallDepth || 60, wallDepthRight || 60)
        : showRightWall ? (wallDepthRight || 60) : (wallDepth || 60);
};

function HumanSilhouette({ position }) {
    const [texture, setTexture] = useState(null);

    useEffect(() => {
        const img = new window.Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const d = imgData.data;
            for (let i = 0; i < d.length; i += 4) {
                if (d[i] > 200 && d[i + 1] > 200 && d[i + 2] > 200) d[i + 3] = 0;
            }
            ctx.putImageData(imgData, 0, 0);
            setTexture(new THREE.CanvasTexture(canvas));
        };
        img.src = silhouetteImg;
    }, []);

    if (!texture) return null;

    const h = 1.75;
    const w = h * 0.34;

    return (
        <group position={position}>
            <mesh position={[0, h / 2, 0]}>
                <planeGeometry args={[w, h]} />
                <meshBasicMaterial map={texture} transparent opacity={0.22} side={THREE.DoubleSide} depthWrite={false} />
            </mesh>
            <Line points={[[w / 2 + 0.06, 0, 0], [w / 2 + 0.06, h, 0]]} color="#b0bec5" lineWidth={1} />
            <ArrowHead position={[w / 2 + 0.06, h, 0]} dir="up" color="#b0bec5" />
            <ArrowHead position={[w / 2 + 0.06, 0, 0]} dir="down" color="#b0bec5" />
            {/* Ant pačios linijos (ne šalia), fonas sutampa su 3D scenos fonu
                (žr. #10 ROOM_STYLE_CONFIGS numatytoji '#fafaf9'), kad tekstas
                "perkirstų" liniją, o ne kabėtų atskirai (žr. /admin/bugs
                "175cm turi būti ant linijos ir su background spalva tokia
                kaip ir 3D fonas"). */}
            <Html position={[w / 2 + 0.06, h / 2, 0]} center>
                <div style={{ fontSize: 11, color: '#b0bec5', fontFamily: 'sans-serif', whiteSpace: 'nowrap', backgroundColor: '#fafaf9', padding: '1px 4px' }}>175 cm</div>
            </Html>
        </group>
    );
}

// Kliūtis pele TEMPTI 3D vaizde nebegalima — pozicija (X/Y/Z) keičiama TIK per
// redagavimo formą (Pozicija X/Y/Z laukai). Paspaudimas ant kliūties tiesiog
// atidaro tą formą.
//
// Kliūtys su gyliu <= nugarėlės storio (1.8cm) anksčiau visai nesimatydavo —
// jų dėžutė patekdavo TIKSLIAI ten pat arba UŽ nugarėlės/sienos plokštumos,
// tad kamera jos tiesiog nematydavo (užstodavo nepermatoma nugarėlė priešais).
// Kliūtis yra vizualinis ŽYMEKLIS/nuoroda, ne fizinė geometrija — turi būti
// matoma VISADA, nepriklausomai nuo gylio ar to, ar ten yra nugarėlė. Todėl
// depthTest išjungtas ir renderOrder didelis: piešiama VISADA VIRŠUJE, kaip
// overlay, o ne pagal tikrą 3D gylio rikiavimą. Taip pat ryški spalva (ne
// baldo plokščių pilka), kad kliūtis aiškiai išsiskirtų.
const Obstacle3D = ({ data, onEdit }) => {
    const [hovered, setHovered] = useState(false);
    const width = Math.max(0.001, Number(data.width) / 100 || 0);
    const height = Math.max(0.001, Number(data.height) / 100 || 0);
    const depth = Math.max(0.001, Number(data.depth) / 100 || 0);
    const pos = [Number(data.x) / 100 || 0, Number(data.y) / 100 || 0, Number(data.z) / 100 || 0];

    const handlePointerDown = (e) => {
        e.stopPropagation();
        if (onEdit) onEdit(data);
    };

    return (
        <group position={pos}>
            <mesh
                position={[width / 2, height / 2, depth / 2]}
                renderOrder={999}
                onClick={handlePointerDown}
                onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; }}
                onPointerOut={() => { setHovered(false); document.body.style.cursor = ''; }}
            >
                <boxGeometry args={[width, height, depth]} />
                <meshStandardMaterial
                    color="#f97316"
                    roughness={0.4} metalness={0.05}
                    emissive="#f97316" emissiveIntensity={hovered ? 0.55 : 0.35}
                    depthTest={false}
                />
            </mesh>
        </group>
    );
};



const FurnitureMeasurementsOverlay = ({ totalWidth, totalHeight, totalDepth, onDimensionChange, furnitureId, disabled = false, modalOpen = false, displayZ }) => {
    return (
        <group position={[0, 0, displayZ]}>
            <Line points={[[0, -0.2, 0], [totalWidth, -0.2, 0]]} color="#1e293b" lineWidth={1} />
            <ArrowHead position={[totalWidth, -0.2, 0]} dir="right" />
            <ArrowHead position={[0, -0.2, 0]} dir="left" />
            <Measurements
                value={totalWidth}
                hideUnit={true}
                position={[totalWidth / 2, -0.35, 0]}
                onChange={(val) => onDimensionChange(furnitureId, 'width', val)}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
            />

            <Line points={[[-0.4, 0, 0], [-0.4, totalHeight, 0]]} color="#1e293b" lineWidth={1} />
            <ArrowHead position={[-0.4, totalHeight, 0]} dir="up" />
            <ArrowHead position={[-0.4, 0, 0]} dir="down" />
            <Measurements
                value={totalHeight}
                hideUnit={true}
                position={[-0.6, totalHeight / 2, 0]}
                onChange={(val) => onDimensionChange(furnitureId, 'height', val)}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
                rotation={[0, 0, Math.PI / 2]}
            />

            <Line points={[[totalWidth + 0.1, -0.2, 0], [totalWidth + 0.1, -0.2, totalDepth]]} color="#1e293b" lineWidth={1} />
            <ArrowHead position={[totalWidth + 0.1, -0.2, totalDepth]} dir="dot" />
            <ArrowHead position={[totalWidth + 0.1, -0.2, 0]} dir="dot" />

            <Measurements
                value={totalDepth}
                hideUnit={true}
                position={[totalWidth + 0.25, -0.2, totalDepth / 2]}
                onChange={(val) => onDimensionChange(furnitureId, 'depth', val)}
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
            />
        </group>
    );
};


const PositionMeasurements = ({ position, totalWidth, totalHeight, onMove, furnitureId, disabled = false, modalOpen = false, displayZ }) => {
    const distanceLeft = position.x;
    const distanceBottom = position.y;

    const handleLeftChange = (val) => { 
        onMove(furnitureId, val, position.y, position.z);
    };

    const handleBottomChange = (val) => { 
        onMove(furnitureId, position.x, val, position.z);
    };

    return (
        <group position={[0, 0, displayZ]}>
            <Line points={[[0, totalHeight/2, 0], [-distanceLeft, totalHeight/2, 0]]} color="blue" lineWidth={1} dashed dashSize={0.1} gapSize={0.1} />
            <Measurements
                value={distanceLeft} 
                hideUnit={true}
                position={[-distanceLeft / 2, totalHeight/2 + 0.1, 0]}
                onChange={handleLeftChange} 
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
            />

            <Line points={[[totalWidth/2, 0, 0], [totalWidth/2, -distanceBottom, 0]]} color="blue" lineWidth={1} dashed dashSize={0.1} gapSize={0.1} />
            <Measurements
                value={distanceBottom} 
                hideUnit={true}
                position={[totalWidth/2 + 0.1, -distanceBottom / 2, 0]}
                onChange={handleBottomChange} 
                label="cm"
                disabled={disabled}
                modalOpen={modalOpen}
            />
        </group>
    );
};

// Fiksuotas gylio rodyklių vaizdinis ilgis (metrais) — žr. jų naudojimą žemiau.
const ROOM_DEPTH_ARROW_LEN = 0.3;

const WallMeasurementsOverlay = ({ width, height, depth, depthRight, roomShape, onDimensionChange, disabled = false, modalOpen = false, displayZ, activeFurnitureY = 0 }) => {
    const showLeft = roomShape === 0 || roomShape === 1 || roomShape === 2;
    const showRight = roomShape === 1 || roomShape === 3;
    const safeDepthRight = depthRight || 60;
    // Turi atitikti MEASURE_BTNS 'room' mygtuko spalvą (žr. žemiau šiame faile) —
    // vartotojas paprašė, kad kiekvienos matmenų grupės rodyklės/ženkliukai būtų
    // TIKSLIAI tos pačios spalvos, kaip juos įjungiantis mygtukas.
    const lineColor = '#d1d5db';
    const textColor = '#374151';
    // Atstumas nuo patalpos 3D brėžinio krašto iki matmenų linijos — DABAR FIKSUOTAS
    // PIKSELIAIS, ne pasaulio metrais. Ankstesnės versijos (fiksuotas metrų atstumas,
    // vėliau — proporcingas PAČIAM kambario dydžiui widthOffsetScale/heightOffsetScale)
    // abi sprendė tą pačią problemą netiesiogiai ir abi vis tiek sugesdavo: joks FIKSUOTAS
    // pasaulio-koordinačių atstumas negali garantuoti saugaus PIKSELIŲ tarpo, nes kamera
    // (žr. CameraAligner) PATI priartėja/atitolsta priklausomai nuo kambario dydžio IR
    // paddingFactor nustatymų — tas pats metrų atstumas skirtingu metu ekrane užima
    // skirtingą pikselių skaičių (žr. /admin/bugs "pakeitus patalpos gylius jų matmenys
    // pasislepia po sienomis" — tai buvo TIESIOGINĖ CameraAligner paddingFactor sumažinimo
    // pasekmė). Vartotojas: "ar galima tiesiog kazkaip fiksuoti patalpos krastus ir nuo ju
    // visada standartiskai kazkiek atitraukti atstumu linijas?" — t.y. PIKSELIAIS, ne
    // metrais. Sprendimas: kiekvieną kadrą perskaičiuojame, kiek pasaulio metrų atitinka
    // vieną ekrano pikselį TIKSLIAI ŠIUO momentu (priklauso nuo camera.fov ir atstumo nuo
    // kameros iki šio overlay Z plokštumos), tada konvertuojame NORIMĄ pikselių tarpą į
    // atitinkamus metrus. Rezultatas: linijos VISADA tiksliai tiek pat pikselių nuo
    // brėžinio krašto, nepriklausomai nuo kambario dydžio AR kameros priartinimo —
    // senasis widthOffsetScale/heightOffsetScale "proporcingumas mažam kambariui" tampa
    // NEBEREIKALINGAS (jį automatiškai užtikrina pati kameros priartinimo logika).
    const { camera: overlayCamera, size: overlaySize } = useThree();
    const MEASUREMENT_LINE_PIXEL_GAP = 65;
    const verticalFovRad = (overlayCamera.fov * Math.PI) / 180;
    // Atstumas nuo kameros iki ŠIO overlay Z plokštumos (displayZ) — kamera visada žiūri
    // tiesiai -Z kryptimi (žr. CameraAligner), tad vien Z skirtumas pakankamai tiksliai
    // atspindi "gylį" perspektyvos skaičiavimui. Math.max apsaugo nuo dalybos iš ~0 /
    // neigiamo atstumo degeneruoto atvejo (kamera už/ant pačios plokštumos).
    const distToOverlayPlane = Math.max(0.5, overlayCamera.position.z - displayZ);
    const worldPerPixel = (2 * distToOverlayPlane * Math.tan(verticalFovRad / 2)) / Math.max(1, overlaySize.height);
    const PIXEL_OFFSET_M = MEASUREMENT_LINE_PIXEL_GAP * worldPerPixel;
    // Ankstesnis papildomas ~10px tarpas ČIA pašalintas — FurnitureGroup.jsx
    // `floorPixelOffsetM` dabar PATS įskaičiuoja ženkliuko dydį atitinkantį tarpą
    // (GAP_Y_M, žr. ten) nuo ŠITO, nepakeisto PIXEL_OFFSET_M — dviejų atskirų
    // "papildomo tarpo" mechanizmų kartu laikymas tik dubliuotų/painiotų, kiek realiai
    // tarpo yra tarp dviejų linijų.
    const WIDTH_LINE_OFFSET = -PIXEL_OFFSET_M;
    // BENDRA TAISYKLĖ (vartotojas: "baldo ir laisvumo tarpeliu matmenys butu arciau
    // patalpos o patalpos matmenys toliau... noretusi vientisumo" — anksčiau kiekviena
    // ašis turėjo SAVO, nesuderintą tvarką, kas atrodė nenuosekliai): Baldo/Laisvumo
    // VISADA ARČIAU realaus 3D objekto, Patalpos — VISADA TOLIAU. Pločio eilutė (žr.
    // WIDTH_LINE_OFFSET aukščiau, nekeista) šią taisyklę jau atitiko (FurnitureGroup.jsx
    // floorPixelOffsetM = roomPixelOffsetM - GAP_Y_M, t.y. mažesnis/arčiau). AUKŠČIO
    // ašis (žemiau) PIRMIAU buvo ATVIRKŠČIAI (Patalpos arčiau, Baldo toliau) — bet jos
    // negalima tiesiog "apversti" tuo pačiu būdu: Baldo aukščio linijai REIKIA tik
    // nedidelio, sienos storį dengiančio postūmio (wallClearanceM), o Patalpos linija
    // turi stovėti DAR TOLIAU už TĄ, su ženkliuko plotį dengiančiu GAP_X_M tarpu.
    // FurnitureGroup.jsx pixelOffsetM naudoja TĄ PATĮ skaičiavimą (dubliuota, kaip ir
    // MEASUREMENT_LINE_PIXEL_GAP jau dubliuojama tarp abiejų failų).
    // KLAIDA (rasta vartotojo screenshot'u — "baldo matmenys susikerta su patalpos
    // siena"): ankstesnė formulė (`WALL_CLEARANCE_PX=30`) laikė sieną BEGALINIO PLONUMO
    // plokštuma ties X=0 — bet Wall.jsx siena TURI TIKRĄ 10cm storį (žr. ten `wt=0.1`),
    // nusidriekdama nuo X=0 (vidinis, kambario pusės paviršius) iki X=-0.1 (IŠORINIS
    // paviršius). Ženkliukas (centruotas <Html center>, PATS turi plotį) savo ARTIMIAUSIU
    // (kambario link nukreiptu) kraštu nutolsta nuo savo inkaro taško (ne tik centro) per
    // pusę savo pločio — su senu 30px postūmiu šis artimiausias kraštas skaičiuojant
    // atsidūrė X=-0.054, T.Y. VIDUJE sienos tūrio [-0.1, 0], ne už jo! Teisinga formulė:
    // WALL_THICKNESS_M (TIKRA, pastovi pasaulio-metrų reikšmė, Wall.jsx `wt` kopija, NE
    // pikselių įvertis — sienos storis fiziškai nekinta priklausomai nuo kameros/lango)
    // + ženkliuko PUSĖS pločio ir norimo matomo tarpo (abu pikseliais-pagrįsti, kaip
    // visur kitur šiame faile).
    const WALL_THICKNESS_M = 0.1; // Wall.jsx "wt" konstantos kopija
    const BADGE_WIDTH_PX = 38;
    const BADGE_HEIGHT_PX = 20;
    const VISIBLE_GAP_PX = 10;
    const wallClearanceM = WALL_THICKNESS_M + (BADGE_WIDTH_PX / 2 + VISIBLE_GAP_PX) * worldPerPixel;
    const GAP_X_M = (BADGE_WIDTH_PX + VISIBLE_GAP_PX) * worldPerPixel;
    const GAP_Y_M = (BADGE_HEIGHT_PX + VISIBLE_GAP_PX) * worldPerPixel;
    const HEIGHT_LINE_OFFSET = -(wallClearanceM + GAP_X_M);
    const DEPTH_LINE_OFFSET = PIXEL_OFFSET_M;
    // "Patalpos gylis" (kairė/dešinė) — TA PATI bendra taisyklė: Patalpos TOLIAU už
    // Baldo gylio ženkliuką (FurnitureGroup.jsx depthRowOffsetM, dabar lygus
    // roomPixelOffsetM, t.y. "arčiau") — PLIUS ženkliuko aukštį dengiantis GAP_Y_M.
    // Y pati (ne vien postūmio dydis) toliau seka AKTYVAUS BALDO Y (ne visada nuo
    // grindų Y=0), kad sutaptų su FurnitureGroup.jsx `floorY`/`depthRowY` aukščiu ir
    // nelįstų ant paties baldo 3D modelio, kai jis pakabintas aukščiau grindų.
    const depthLineY = -activeFurnitureY - (PIXEL_OFFSET_M + GAP_Y_M);

    return (
        <group position={[0, 0, displayZ]}>
            <Line points={[[0, WIDTH_LINE_OFFSET, 0], [width/100, WIDTH_LINE_OFFSET, 0]]} color={lineColor} lineWidth={1} />
            <ArrowHead position={[width/100, WIDTH_LINE_OFFSET, 0]} dir="right" color={lineColor} />
            <ArrowHead position={[0, WIDTH_LINE_OFFSET, 0]} dir="left" color={lineColor} />
            <Measurements
                value={width / 100}
                position={[width / 100 / 2, WIDTH_LINE_OFFSET, 0]}
                onChange={(val) => onDimensionChange(null, 'width', val)}
                hideUnit={true}
                disabled={disabled}
                modalOpen={modalOpen}
                variant="room"
                title="Patalpos plotis"
                bgColor={lineColor}
                textColor={textColor}
            />

            <Line points={[[HEIGHT_LINE_OFFSET, 0, 0], [HEIGHT_LINE_OFFSET, height/100, 0]]} color={lineColor} lineWidth={1} />
            <ArrowHead position={[HEIGHT_LINE_OFFSET, height/100, 0]} dir="up" color={lineColor} />
            <ArrowHead position={[HEIGHT_LINE_OFFSET, 0, 0]} dir="down" color={lineColor} />
            <Measurements
                value={height / 100}
                position={[HEIGHT_LINE_OFFSET, height / 100 / 2, 0]}
                onChange={(val) => onDimensionChange(null, 'height', val)}
                hideUnit={true}
                disabled={disabled}
                modalOpen={modalOpen}
                variant="room"
                title="Patalpos aukštis"
                bgColor={lineColor}
                textColor={textColor}
            />

            {/* Gylio rodyklių ilgis fiksuotas — nepriklauso nuo realaus patalpos
                gylio (žr. ClickUp "Gylio rodyklės... gali būti fiksuoto ilgio").
                Y ašis (depthLineY) DABAR dinamiškai seka aktyvaus baldo gylio linijos
                aukštį (žr. FurnitureGroup.jsx FurnitureMeasurementsOverlay: linija ten
                stovi ties floorY+0.06, o floorY priklauso nuo baldo apatinio laisvumo)
                — anksčiau visada Y=0 (patalpos grindys), nepriklausomai nuo baldo
                padėties, todėl dvi linijos vizualiai nesutapdavo (žr. ClickUp "2.0
                Baldas. Gylio matmenų vieta. Truputį pagražinti" — "Patalpos gylį
                nuleisti į apačią. Darom tokiame pačiame aukštyje kaip ir baldo linija").
                Kai baldo dar nėra (Patalpa žingsnis), Builder3D.jsx perduoda 0
                (numatytoji reikšmė), tad senas elgesys išlieka nepakitęs. */}
            {showLeft && (
                <>
                    <Line points={[[-DEPTH_LINE_OFFSET, depthLineY, 0], [-DEPTH_LINE_OFFSET, depthLineY, ROOM_DEPTH_ARROW_LEN]]} color={lineColor} lineWidth={1} />
                    <ArrowHead position={[-DEPTH_LINE_OFFSET, depthLineY, 0]} dir="dot" color={lineColor} />
                    <ArrowHead position={[-DEPTH_LINE_OFFSET, depthLineY, ROOM_DEPTH_ARROW_LEN]} dir="dot" color={lineColor} />
                    {/* Y (ne Z) atskirtas nuo taškų — kamera žiūri TIESIAI Z ašimi, tad abu
                        taškai (Z=0 ir Z=ROOM_DEPTH_ARROW_LEN) IR ženkliukas tarp jų (Z
                        vidurys) suplokštėja į beveik TĄ PATĮ ekrano tašką (perspektyvos
                        sutrumpėjimas), o ženkliuko fiksuoto pikselių dydžio kortelė
                        persidengia su tašku, atrodydama sugadinta (žr. /admin/bugs "baldo
                        matmenys pasislepia po patalpos sienomis ir grindimis" — iš tikrųjų
                        tai nebuvo slėpimasis už geometrijos, o šis Z-suplokštėjimo
                        persidengimas, kurį kameros pakeitimas TIK padidino, nes kamera dabar
                        dažnai stovi toliau). Z ašis kamerai visada suplokštėja, tad tik Y
                        atskyrimas (ne Z) realiai išsprendžia. */}
                    <Measurements
                        value={depth/100}
                        position={[-DEPTH_LINE_OFFSET, depthLineY - 0.16, ROOM_DEPTH_ARROW_LEN / 2]}
                        onChange={(val) => onDimensionChange(null, 'depth', val)}
                        hideUnit={true}
                        disabled={disabled}
                        modalOpen={modalOpen}
                        variant="room"
                        title="Patalpos gylis (kairė)"
                        bgColor={lineColor}
                        textColor={textColor}
                    />
                </>
            )}

            {showRight && (
                 <>
                    <Line points={[[width/100 + DEPTH_LINE_OFFSET, depthLineY, 0], [width/100 + DEPTH_LINE_OFFSET, depthLineY, ROOM_DEPTH_ARROW_LEN]]} color={lineColor} lineWidth={1} />
                    <ArrowHead position={[width/100 + DEPTH_LINE_OFFSET, depthLineY, 0]} dir="dot" color={lineColor} />
                    <ArrowHead position={[width/100 + DEPTH_LINE_OFFSET, depthLineY, ROOM_DEPTH_ARROW_LEN]} dir="dot" color={lineColor} />
                    <Measurements
                        value={safeDepthRight/100}
                        position={[width/100 + DEPTH_LINE_OFFSET, depthLineY - 0.16, ROOM_DEPTH_ARROW_LEN / 2]}
                        onChange={(val) => onDimensionChange(null, 'depthRight', val)}
                        hideUnit={true}
                        disabled={disabled}
                        modalOpen={modalOpen}
                        variant="room"
                        title="Patalpos gylis (dešinė)"
                        bgColor={lineColor}
                        textColor={textColor}
                    />
                </>
            )}
        </group>
    );
};


const CameraAligner = ({ cabinets, basisHeight, onReady, currentComponentId, wallWidth, wallHeight, wallDepth, wallDepthRight, roomShape, activeFurnitureId, activeStep, obstacles, wallThickness, hideWall, measureRoom, activeFurnitureY }) => {
    const { camera, controls, gl, size, invalidate } = useThree();
    useLayoutEffect(() => {
        if (!controls || !gl || !size.width || !size.height) return;

        let allMinX = Infinity, allMaxX = -Infinity, allMinY = Infinity, allMaxY = -Infinity, allMinZ = Infinity, allMaxZ = -Infinity;

        // /admin/bugs "1.0 Patalpa. Keičiant gylį (dešinėje), šokinėja patalpos pozicija
        // ekrane." — šis skaičiavimas iki šiol naudojo TIK `wallDepth` (kairė), visiškai
        // ignoruodamas `wallDepthRight` — net kai dešinioji siena REALIAI piešiama gilesnė
        // (žr. Wall.jsx maxFloorDepth, kuri grindis/lubas pratęsia iki didesnės iš dviejų).
        // Kadangi `centerZ` žemiau = allMaxZ (patalpos PRIEKIS, prie kurio kamera visada
        // fiksuojama — žr. zBias komentarą žemiau), kol dešinysis gylis buvo ignoruojamas,
        // kamera TOLIAU sekė kairįjį gylį kaip "priekį", o reali (gilesnė) geometrija dešinėje
        // likdavo nebesutampanti su kameros tikslu — didinant dešinįjį gylį kamera "nepastebi"
        // pokyčio, tad viskas kadre atrodo pasislinkę. Ta pati showLeft/showRight logika, kaip
        // Wall.jsx (TIK realiai matomos pusės gylis turi įtakos — nerodomos sienos
        // sukonfigūruotas gylis neturi reikšmės, kitaip kadras "iškištų" pagal nematomą pusę).
        // Bendra funkcija (žr. getEffWallDepth aukščiau) — anksčiau ČIA buvusi savarankiška
        // kopija pati savaime buvo teisinga, bet DVI kitos šio failo vietos (HumanSilhouette,
        // measurementsZ) turėjo SAVO, nesusietas (ir vienu atveju — klaidingas) kopijas.
        const effWallDepth = getEffWallDepth(roomShape, wallDepth, wallDepthRight);

        // Tikslus gylio ženkliuko Y postūmis — DUBLIUOJA WallMeasurementsOverlay (žr.
        // ten depthLineY/PIXEL_OFFSET_M/GAP_Y_M, ~eil. 275-329) formulę, kad allMinY
        // žemiau rezervuotų TIKSLIAI tiek vietos, kiek ženkliukui realiai reikia — ne
        // senesnį, apytikslį -0.35 įvertį (žr. žemiau Builder3D.jsx scope'e, naudojamą
        // TIK kaip apytikslę atramą prieš CameraAligner paleidimą). Senas apytikslis
        // skirtumas (~0.467m vs 0.35m, t.y. ~11.7cm trūkumas) nebuvo pastebimas
        // dideliame/plačiai pritrauktame lange (liko paraštės), bet mažame/žemesniame
        // lange ženkliukui jau nebeužtenka vietos — jis apsikerpa ekrano apačioje.
        const marginVerticalFovRad = (camera.fov * Math.PI) / 180;
        const marginDistToPlane = Math.max(0.5, camera.position.z - (effWallDepth / 100 + 0.01));
        const marginWorldPerPixel = (2 * marginDistToPlane * Math.tan(marginVerticalFovRad / 2)) / Math.max(1, size.height);
        // 65 (MEASUREMENT_LINE_PIXEL_GAP) + 20 (BADGE_HEIGHT_PX) + 10 (VISIBLE_GAP_PX) —
        // tos pačios konstantos, kaip WallMeasurementsOverlay (dubliuota, kaip ir
        // kitos šio tipo konstantos tarp šio ir to komponento).
        const preciseDepthLineY = -activeFurnitureY - 95 * marginWorldPerPixel;

        // Kai siena nerodoma (hideWall — švarus baldo fotografavimas be patalpos konteksto),
        // kamera turi glaustis prie PAČIO BALDO ribų, o ne prie visos patalpos (wallWidth/
        // wallHeight) — priešingu atveju baldas nuotraukoje atrodo mažas, apsuptas tuščios
        // erdvės, net jei siena ir taip niekada nepieštuma.
        if (!hideWall) {
            allMinX = Math.min(allMinX, 0);
            allMaxX = Math.max(allMaxX, (wallWidth || 600) / 100);
            // WallMeasurementsOverlay gylio ženkliukas (kai measureRoom) piešiamas ties
            // depthLineY-0.16 (žr. ten Measurements position), t.y. ŽEMIAU grindų —
            // bet šis skaičiavimas apie tai nežinojo (allMinY visada buvo lygiai 0), tad
            // mažuose kambariuose ženkliukui niekada nebuvo rezervuota vietos kadre (žr.
            // /admin/bugs "Reikia bent minimalaus atstumo tarp input laukų ir ekrano
            // apačioje visais atvejais"). 0.15 — papildoma saugos paraštė virš ženkliuko
            // paties pikselinio aukščio.
            allMinY = Math.min(allMinY, measureRoom ? (preciseDepthLineY - 0.16 - 0.15) : 0);
            allMaxY = Math.max(allMaxY, (wallHeight || 250) / 100);
            allMinZ = Math.min(allMinZ, -wallThickness);
            allMaxZ = Math.max(allMaxZ, effWallDepth / 100);

            // /admin/bugs "3D vaizdas per mažas, o laisvos vietos turim" — paddingFactor
            // žemiau sumažintas, bet TAS PATS bendras paraštės dydis anksčiau NETYČIA
            // dengė ir WallMeasurementsOverlay plotį/aukštį/gylio ženkliukus (kairėje/
            // apačioje, žr. ten HEIGHT_LINE_OFFSET/WIDTH_LINE_OFFSET) IR HumanSilhouette
            // (dešinėje) — jie PATYS NIEKADA nebuvo įskaičiuoti į allMin/allMax, tiesiog
            // "tilpdavo" į didelę bendrą 1.4x paraštę. Sumažinus tą paraštę, jie būtų
            // apkarpomi. Dabar jų TIKRAS pasaulio-koordinačių užimamas plotas (tos
            // pačios formulės kaip WallMeasurementsOverlay/HumanSilhouette aukščiau)
            // ĮSKAIČIUOJAMAS tiesiogiai — kad sumažinta bendra paraštė liktų TIK
            // vizualiai "laisva" (žr. žemiau paddingFactor), o ne kerta šiuos elementus.
            const roomWMeters = Math.max(0.001, (wallWidth || 600) / 100);
            const roomHMeters = Math.max(0.001, (wallHeight || 250) / 100);
            const widthOffsetScale = Math.min(1, Math.max(0.3, roomHMeters / 2.5));
            const heightOffsetScale = Math.min(1, Math.max(0.3, roomWMeters / 2));
            const OVERLAY_SAFETY_MARGIN = 0.1;
            // SVARBU: kairės (aukščio ženklas) ir dešinės (žmogaus siluetas) paraštės
            // pločiai NEVIENODI — jei allMinX/allMaxX pritaikomi asimetriškai (tik tiek,
            // kiek konkrečiai KAIRĖ/DEŠINĖ pusė reikalauja), žemiau esantis centerX
            // (allMinX+allMaxX)/2 pasislenka NUO tikro patalpos vidurio, taigi IR
            // kamera X ašimi pasislenka į šoną — net žiūrint TIESIAI (jokio pasukimo),
            // tai sugadina gylio rodyklių (ištįsusių Z ašimi) perspektyvą ŠONINIŲ sienų
            // atžvilgiu, jos imasi vizualiai "lįsti" už sienos (žr. vartotojo screenshot
            // su abiem gylio sienom "matmenys pasislepe po/uz sienu"). Todėl abi pusės
            // IŠPLEČIAMOS VIENODAI — pagal DIDESNĮ iš dviejų reikalavimų — kad centerX
            // visada liktų TIKSLIAI patalpos viduryje (kamera nejuda į šoną), net jei tai
            // reiškia, kad kairėje lieka daugiau neišnaudotos vietos nei būtina ten.
            const leftMargin = 0.6 * heightOffsetScale + OVERLAY_SAFETY_MARGIN;
            // HumanSilhouette: w=1.75*0.34=0.595 pločio, centruota ties wallWidth+0.65,
            // + jos pačios aukščio linija/ženkliukas w/2+0.06 į dešinę nuo centro.
            const rightMargin = 0.65 + (1.75 * 0.34) / 2 + 0.06 + OVERLAY_SAFETY_MARGIN;
            const symmetricMargin = Math.max(leftMargin, rightMargin);
            allMinX = Math.min(allMinX, -symmetricMargin);
            allMaxX = Math.max(allMaxX, roomWMeters + symmetricMargin);
            // WIDTH_LINE_OFFSET ženklas (plotis) stovi TIK apačioje — nėra jam
            // simetriško elemento viršuje, tad čia nereikia centerY stumti, tiesiog
            // įskaičiuojama į allMinY kaip buvo.
            allMinY = Math.min(allMinY, -0.55 * widthOffsetScale - OVERLAY_SAFETY_MARGIN);
        }

        cabinets.forEach(cab => {
            allMinX = Math.min(allMinX, Number(cab.x) || 0);
            allMaxX = Math.max(allMaxX, (Number(cab.x) || 0) + (Number(cab.width) || 0));
            allMinY = Math.min(allMinY, Number(cab.y) || 0);
            allMaxY = Math.max(allMaxY, (Number(cab.y) || 0) + (Number(cab.height) || 0));
            allMinZ = Math.min(allMinZ, Number(cab.z) || 0);
            allMaxZ = Math.max(allMaxZ, (Number(cab.z) || 0) + (Number(cab.depth) || 0));
        });

        obstacles.forEach(obs => {
            const obsX = Number(obs.x) / 100 || 0;
            const obsY = Number(obs.y) / 100 || 0;
            const obsZ = Number(obs.z) / 100 || 0;
            const obsW = Number(obs.width) / 100 || 0;
            const obsH = Number(obs.height) / 100 || 0;
            const obsD = Number(obs.depth) / 100 || 0;

            allMinX = Math.min(allMinX, obsX);
            allMaxX = Math.max(allMaxX, obsX + obsW);
            allMinY = Math.min(allMinY, obsY);
            allMaxY = Math.max(allMaxY, obsY + obsH);
            allMinZ = Math.min(allMinZ, obsZ);
            allMaxZ = Math.max(allMaxZ, obsZ + obsD);
        });


        // Kamera turi likti fiksuota ties patalpos PRIEKIU VISUOSE žingsniuose (ne tik
        // Patalpa) — pagal vartotojo patikslinimą (žr. ClickUp "1.0 Patalpa. Keičiant gylį,
        // juda patalpos priekis" + tolesnį "patalpos kameros vaizdas turi būti visur
        // vienodas") "priekis" yra ATVIROJI/artimiausia kamerai pusė (didžiausia Z reikšmė —
        // Wall.jsx grindys/lubos/šoninės sienos baigiasi ties wallDepth), o "galinė siena"
        // (kietas skydas, prie kurio stovi baldai, Wall.jsx z≈0) yra tai, kas TURI judėti
        // keičiant gylį. Anksčiau ŠI logika taikyta TIK 'Patalpa' žingsniui (zBias=1), o kiti
        // žingsniai (Baldas/Išorė/Vidus) liko prie senojo vidurio taško (zBias=0.5) — kadangi
        // abu skaičiavimai naudoja TĄ PATĮ wallDepth, bet skirtingą daugiklį, kamera
        // "šokdavo" į kitą Z pozicją pereinant iš Patalpa į kitą žingsnį, o kartu su ja ir
        // patalpos matmenų sietuvas (žr. FurnitureGroup.jsx measurementsZ) nebesutapdavo su
        // kameros rėmu (žr. vartotojo pranešimą "perėjus į Baldas puslapį, vaizdas
        // susigadina... kameros vaizdas turi būti visur vienodas"). zBias=1 dabar taikoma
        // VISUR (išskyrus hideWall — švarų baldo fotografavimą be patalpos konteksto, kur
        // simetriškas vidurio kadravimas lieka prasmingas), tad kameros TIKSLINIS taškas —
        // ir per jį visas kadras — visuose žingsniuose sutampa.
        const zBias = hideWall ? 0.5 : 1;
        let centerX, centerY, centerZ;
        if (allMinX === Infinity) {
            centerX = (wallWidth / 100) / 2;
            centerY = (wallHeight / 100) / 2;
            centerZ = (wallDepth / 100) * zBias;
        } else {
            centerX = allMinX + (allMaxX - allMinX) / 2;
            centerY = allMinY + (allMaxY - allMinY) / 2;
            centerZ = allMinZ + (allMaxZ - allMinZ) * zBias;
        }


        // /admin/bugs "1.0 Patalpa. AutoZoom. Kai maži patalpos matmenys - AutoZoomina
        // - nebetelpa matmenys į ekraną" — šis skaičiavimas pritaiko kamerą TIK prie
        // 3D geometrijos ribų, bet matmenų ženkliukai (<Html> overlay'ai aplink
        // kambarį/baldą) turi FIKSUOTĄ pikselių dydį ir FIKSUOTĄ pasaulio-koordinačių
        // atstumą nuo brėžinio (žr. WallMeasurementsOverlay WIDTH_LINE_OFFSET ir kt.
        // žemiau). Dideliam kambariui tas fiksuotas atstumas yra nedidelė dalis viso
        // kadro, bet labai mažam kambariui (pvz. 100×100cm, vartotojo pateiktas
        // pavyzdys) kamera priartėja taip stipriai, kad tas pats fiksuotas atstumas
        // (ir jo ženkliuko pikselių dydis) viršija visą likusią kadro paraštę — žr.
        // vartotojo screenshot: "Patalpos aukštis" dar matosi, bet apatiniai gylio
        // ženkliukai beveik iškrenta už kadro. Neleidžiant EFEKTYVIAM (zoom'ui
        // naudojamam) pločiui/aukščiui susitraukti žemiau MIN_ZOOM_TARGET, net jei
        // TIKRAS kambarys mažesnis, kamera visada palieka pakankamai paraštės.
        const MIN_ZOOM_TARGET = 1.5;
        const targetWidth = Math.max(allMaxX - allMinX, MIN_ZOOM_TARGET);
        const targetHeight = Math.max(allMaxY - allMinY, MIN_ZOOM_TARGET);
        const targetDepth = allMaxZ - allMinZ;

        // Use r3f's own reactive size instead of reading gl.domElement.clientWidth/Height
        // directly — on the very first render the canvas may not have its final layout
        // size yet (browser still settling flex/grid), which skewed the aspect ratio and
        // produced a badly-framed camera until something else (e.g. a step change) forced
        // this effect to re-run. r3f's `size` updates reactively via ResizeObserver, so
        // this recalculates correctly once the canvas reaches its real size.
        const canvasAspectRatio = size.width / size.height;

        const paddingFactor = hideWall ? 1.25 : 1.12;

        let distance;
        const verticalFov = (camera.fov * Math.PI) / 190;

        const distanceByHeight = (targetHeight / 2 / Math.tan(verticalFov / 2)) * paddingFactor;
        const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * canvasAspectRatio);
        const distanceByWidth = (targetWidth / 2 / Math.tan(horizontalFov / 2)) * paddingFactor;
        const distanceByDepth = targetDepth * paddingFactor;

        // /admin/bugs "Dabar labai atitolina vaizdą pakeitus patalpos gylį" — nebent
        // hideWall (švarus baldo fotografavimas, žr. žemiau), distanceByDepth NETRAUKIAMA
        // į max(): kamera visad stovi ties patalpos PRIEKIU (centerZ=allMaxZ, žr. zBias
        // komentarą), o `distance` tiesiog PRIDEDAMA prie to +Z kryptimi (į vartotoją, TOLYN
        // nuo likusios patalpos geometrijos) — patalpos gylis (kiek ji tęsiasi -Z kryptimi,
        // TOLIAU nuo kameros) jai įtakos neturi, nepriklausomai koks jis didelis, nes galinė
        // siena tiesiog natūraliai atrodo mažesnė/tolimesnė dėl perspektyvos, kamerai
        // nereikia papildomai trauktis. Anksčiau `distanceByDepth` vis tiek patekdavo į
        // max() ir, kol `targetDepth` sekdavo TIK (mažesnį) kairįjį gylį, retai kada realiai
        // laimėdavo prieš distanceByWidth/Height — bet FIX'inus ankstesnį bug'ą ("šokinėja
        // patalpos pozicija", žr. aukščiau effWallDepth=max(kairė,dešinė)), targetDepth dabar
        // TEISINGAI gali tapti didelis (pvz. 400cm gylis dešinėje), ir distanceByDepth
        // pradėjo DOMINUOTI, nustumdamas kamerą gerokai toliau, nei reikia vien
        // pločiui/aukščiui sutalpinti — būtent tai vartotojas ir pastebėjo. hideWall atveju
        // (žr. žemiau) kamera centruojama ties GYLIO VIDURIU (zBias=0.5, ne priekiu), tad
        // ten distanceByDepth lieka prasmingas — be jo gilus baldas galėtų atsidurti per
        // arti, dalis jo "iškištų" už kameros.
        distance = hideWall
            ? Math.max(distanceByHeight, distanceByWidth, distanceByDepth)
            : Math.max(distanceByHeight, distanceByWidth);

        // /admin/bugs "3D vaizdas per mažas" — stati (aukštesnė nei platesnė) patalpa
        // plačiame (landscape) kadre MATEMATIŠKAI negali vienu metu (a) niekada nekirpti
        // aukščio IR (b) užpildyti pločio — tai "contain vs cover" prieštara, ne klaida
        // (patikrinta: jokia FOV/atstumo formulės variacija šito neapeina, kai kambario
        // kraštinių santykis skiriasi nuo kadro). Vartotojas: "nenukirpti 3D vaizde esančių
        // elementų/objektų, jie visada turi būti matomi" — griežtesnis reikalavimas nei
        // anksčiau bandytas "~8% nuleidimas tik ženkliukų paraštei" kompromisas (TAS, kartu
        // su žemiau pašalintu asimetrišku allMinX/allMaxX, realiai siaurame/žemame lange
        // kartais pristumdavo gylio ženkliukus prie pat sienos — žr. vartotojo screenshot
        // "matmenys pasislepę po/už sienų"). Dabar `distance` NIEKADA nemažinamas žemiau
        // pilno Math.max(distanceByHeight, distanceByWidth) — visa "kvėpavimo" erdvė ateina
        // IŠ allMinX/allMaxX/allMinY paraščių (žr. aukščiau) ir žemo paddingFactor, abu
        // visada ĮSKAIČIUOTI į targetWidth/targetHeight PRIEŠ šią distance'o formulę, ne
        // "apkarpomi" po jos.
        // 2m minimalus atstumas prasminga tik kai kadre yra visa patalpa (didelė erdvė,
        // per arti pastačius kamerą regis absurdiška) — švariam baldo fotografavimui be
        // sienos (hideWall) šis "grindys" priverstinai nustumdavo kamerą per toli nuo
        // MAŽŲ baldų (pvz. komodos, spintelės), todėl jie nuotraukoje atrodydavo maži.
        distance = Math.max(distance, hideWall ? 0.4 : 2);

        controls.target.set(centerX, centerY, centerZ);
        camera.position.set(centerX, centerY, centerZ + distance);
        camera.updateProjectionMatrix();
        controls.update();
        // frameloop="demand" (žr. žemiau <Canvas>) nebepiešia kadro automatiškai —
        // ši kameros pozicija keičiama IMPERATYVIAI (camera.position.set), ne per
        // R3F valdomą JSX prop'ą, tad be šio invalidate() iškvietimo naujas
        // kameros kadras NIEKADA nebūtų nupieštas, kol vartotojas pats kažką
        // nepaliestų (pvz. nepasuktų OrbitControls, kuris savaime invalidate()
        // kviečia) — ekranas liktų su SENU vaizdu, nors kamera realiai jau pajudėjo.
        invalidate();
        const timer = setTimeout(() => onReady(true), 50);
        return () => clearTimeout(timer);
        // Pastaba dėl priklausomybių: `activeFurnitureId`/`currentComponentId` sąmoningai
        // NEĮTRAUKTI — jie keičiasi vien PAŽYMĖJUS/atžymėjus baldą ar perjungus šoninio
        // meniu skirtuką, o ne dėl bet kokio realaus turinio/matmenų pasikeitimo, tad jų
        // buvimas čia priversdavo kamerą persicentruoti praktiškai po kiekvieno paspaudimo
        // (net paspaudus šalia baldo, kas ji atžymi) — žr. ClickUp "Paveikslėlio valdymas.
        // Išjungti baldo autozoominą, kai paspaudi su pele ant kitų ekrano vietų". `cabinets`
        // pakeista į `cabinets.length` ta pačia priežastimi — masyvo REFERENCIJA keičiasi
        // po VISIŠKAI kiekvieno redagavimo (net vienos lentynos pridėjimo), o mums čia
        // svarbu tik STRUKTŪRINIS pokytis (baldas pridėtas/pašalintas), ne kiekviena smulki
        // redakcija; efekto VIDUJE vis tiek naudojami PATYS NAUJAUSI `cabinets` duomenys
        // (closure), tik PERSKAIČIAVIMO sąlyga susiaurinta. Tyčinis autofit VISADA lieka
        // pasiekiamas per "F" klavišą (žr. ZoomToFit žemiau).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cabinets.length, basisHeight, camera, controls, gl, size.width, size.height, onReady, wallWidth, wallHeight, wallDepth, wallDepthRight, roomShape, activeStep, obstacles, wallThickness, hideWall, measureRoom, activeFurnitureY]);
    return null;
};


// ── ZoomToFit ─────────────────────────────────────────────────────────────────
// Listens to fitTrigger (incrementing counter); when it changes zooms camera to fit all content.
const ZoomToFit = ({ fitTrigger, cabinets, wallWidth, wallHeight, wallDepth, wallThickness }) => {
    const { camera, controls, gl, size, invalidate } = useThree();
    const prevTrigger = useRef(0);
    useLayoutEffect(() => {
        if (fitTrigger === prevTrigger.current || !controls || !gl || !size.width || !size.height) return;
        prevTrigger.current = fitTrigger;
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        cabinets.forEach(c => {
            const x = Number(c.x) || 0, w = Number(c.width) || 0;
            const y = Number(c.y) || 0, h = Number(c.height) || 0;
            minX = Math.min(minX, x); maxX = Math.max(maxX, x + w);
            minY = Math.min(minY, y); maxY = Math.max(maxY, y + h);
        });
        if (!isFinite(minX)) { minX = 0; maxX = (wallWidth || 600) / 100; minY = 0; maxY = (wallHeight || 250) / 100; }
        const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
        const tw = maxX - minX, th = maxY - minY;
        const aspect = size.width / size.height;
        const fovRad = (camera.fov * Math.PI) / 180;
        const dH = (th / 2 / Math.tan(fovRad / 2)) * 1.3;
        const dW = (tw / 2 / Math.tan(2 * Math.atan(Math.tan(fovRad / 2) * aspect) / 2)) * 1.3;
        const dist = Math.max(dH, dW, 1.5);
        controls.target.set(cx, cy, 0);
        camera.position.set(cx, cy, dist);
        camera.updateProjectionMatrix();
        controls.update();
        // See CameraAligner's identical invalidate() comment — imperative camera
        // move needs an explicit nudge under frameloop="demand".
        invalidate();
    }, [fitTrigger]);
    return null;
};

const CABINET_WALL_THICKNESS = 0.018;

// Mirrors RecursiveSection's split-size math (SingleCabinet3D.jsx) to compute the actual
// width/height of the leaf at `pathStr`, walking down a structure tree from the root size.
// pathStr segments after the leading "0" are child indices, e.g. "0-1-0".
const resolveLeafSize = (rootNode, pathStr, rootWidth, rootHeight, wt) => {
    let node = rootNode;
    let w = rootWidth, h = rootHeight;
    if (!pathStr || pathStr === '0') return { width: w, height: h };
    const indices = pathStr.split('-').slice(1).map(Number);
    for (const idx of indices) {
        if (!node || !node.split || Number.isNaN(idx)) break;
        const isHorizontal = node.split === 'h';
        const N = node.children ? node.children.length : 2;
        if (N === 2) {
            const ratio = node.ratio ?? 0.5;
            const available = isHorizontal ? h - wt : w - wt;
            const child0Size = available * ratio;
            const child1Size = available * (1 - ratio);
            const size = idx === 0 ? child0Size : child1Size;
            if (isHorizontal) h = size; else w = size;
        } else {
            const available = isHorizontal ? h - wt * (N - 1) : w - wt * (N - 1);
            const sizes = node.sizes || Array(N).fill(1 / N);
            const size = (sizes[idx] ?? (1 / N)) * available;
            if (isHorizontal) h = size; else w = size;
        }
        node = node.children ? node.children[idx] : null;
    }
    return { width: w, height: h };
};

const ROOM_STYLE_CONFIGS = [
    { bg: '#fafaf9', floor: '#c8b89a' },  // 0 Numatyta — rgb(250,250,249), projekto standartinė background spalva (žr. #10)
    { bg: '#f0f0ee', floor: '#e8e4de' },  // 1 Balta
    { bg: '#d8d8d8', floor: '#9e9e9e' },  // 2 Pilka
    { bg: '#ddd0c0', floor: '#7d5a2a' },  // 3 Medis
    { bg: '#1a2030', floor: '#252525' },  // 4 Tamsus
    { bg: '#eae0d0', floor: '#b89060' },  // 5 Smėlis
];

const Builder3D = ({
    cabinets = [], setCabinets, backWall, setBackWall, viewMode, setViewMode, activeFurnitureId, onSelect, onMove,
    activeStep,
    onAddShelf, onRemoveShelf, onAddVertical, onRemoveVertical, onDimensionChange,
    wallWidth, wallHeight, 
    wallDepth, wallDepthRight = 60, 
    currentComponentId, selectedCabinetIndex, setSelectedCabinetIndex, setRendererRef, setSceneRef, setCameraRef,
    basisType, basisHeight = 0.1, selectedPlate, sectionOptions, onSectionOptionSelect,
    sectionComponentOffsets, onSectionComponentOffsetChange,
    // Pakelta iš lokalaus useState į Layout.jsx valdomą būseną, kad "Rodyti
    // kontūrus" pasirinkimas patektų į prepareFurnitureData()/settings ir
    // išliktų išsaugius/vėl atidarius baldą (įsk. Default baldą) — žr. ClickUp
    // "3.0 Išorė. Rodyti kontūrus - Default įjungta... pasirinkimas po
    // kažkiek laiko pranyksta".
    showDoorEdges = true, setShowDoorEdges,
    mergedSections = [],
    setMergedSections, doorPlates, onCabinetClick,
    obstacles, onObstacleEdit, onObstacleSelect,
    onDeselectAll,
    selectedDoorPlate,
    doorColors,
    setDoorColors,
    allFurnitureGroupRects,
    onShelfHeightChange,
    coverPlates,
    modalOpen,
    onDoorSelection,
    onDoorRemoval,
    roomShape,
    skirtingType,
    onCabinetWidthChange,
    onInterCabinetResize,
    onCabinetHeightChange,
    leftSideElement,
    rightSideElement,
    onSplitSection,
    splitSectionMode,
    onHorizontalSplit,
    onVerticalSplit,
    onSectionRatioChange,
    onSectionDelete,
    onLeafSectionDelete,
    onNarySectionResize,
    onShelfDelete,
    onInteriorHSplit,
    onInteriorVSplit,
    onInteriorSectionResize,
    onInteriorSectionDelete,
    onInteriorLeafSectionDelete,
    onInteriorNarySectionResize,
    onPromoteAtPanel,
    onDemoteAtPanel,
    panelFrontOffsets,
    onTogglePanelFront,
    doorGroupLabels,
    orbitEnabled = false,
    // Off by default everywhere (matches every existing call site's expectation
    // that dragging never tumbles the camera during editing) — only the new
    // read-only "Žiūrėti 3D" product viewer on /baldai/:code passes this true,
    // to get the wider (not editor-locked) rotation/polar angle limits and to
    // unlock the autoRotate gate below.
    enableFreeRotate = false,
    // "Auto-pasukimas" — kamera pati sukasi aplink baldą be vartotojo veiksmų;
    // substitutes for a pre-rendered product video (IKEA's "Play" thumbnail)
    // without needing an actual video-encoding pipeline. Only meaningful
    // together with enableFreeRotate.
    autoRotate = false,
    // The public /baldai/:code "Žiūrėti 3D" view is presentational ONLY — no
    // drag-to-rotate, no scroll-to-zoom, no pan; it just auto-spins. Off by
    // default everywhere else (the live editor's own zoom/pan stay untouched).
    presentationOnly = false,
    // Suppresses every editor-only overlay (measurement toggle buttons,
    // "Fasadų aukščiai"/"Rodyti dureles" chips, and the default-on measurement
    // labels they control) — for the read-only customer product viewer, which
    // otherwise inherited these purely from passing activeStep="Išorė" (the
    // same step value the live editor uses, which defaults facadeHeights on).
    readOnly = false,
    fitTrigger = 0,
    roomStyle3D = 0,
    hideWall = false,
    furnitureType = 0,
    slidingDoorCount = 2,
    slidingDoorProfile = 0,
    onFramesRendered,
    frameKey,
    // Neprivalomas "praneškite tėviniam" callback'as — selectedShelf PATS lieka
    // lokalus Builder3D state'as (nekeičiame nei jo, nei ~15 esamų setSelectedShelf
    // iškvietimo vietų šiame faile), tik papildomai pranešame kiekvieną pasikeitimą
    // aukštyn, kad Layout.jsx/ConfigPanel galėtų žinoti, kuris skydelis/durelės šiuo
    // metu pažymėtos 3D vaizde (žr. PlatesFillingsAndHandles.jsx eilutės paryškinimą).
    onSelectedShelfChange,
}) => {
    const [doorHandles, setDoorHandles] = useState({});
    const [isModelReady, setIsModelReady] = useState(false);
    const wallThickness = 0.1;
    // Off-screen fiksavimo egzemplioriams (onFramesRendered) r3f Canvas'o vidinis
    // events.connect() efektas yra Canvas'o VAIKO (ne mūsų apgaubiančio div'o) —
    // React commit'ina layout efektus/ref'us medžio POST-ORDER tvarka (pirma
    // vaikai, tik tada tėvai), tad jei eventSource nurodytų į mūsų apgaubiančio
    // div'o useRef, r3f savo connect() paleistų PRIEŠ mūsų ref'ui apskritai
    // spėjant prisirišti — todėl jis visada gaudavo null ir mesdavo "Cannot read
    // properties of null (reading 'addEventListener')". Sprendimas: callback-ref
    // + state — div'as sumontuojamas PIRMĄ kartą be Canvas viduje, jo ref'as
    // suveikia, TADA (kitame render'e) Canvas sumontuojamas su eventSource,
    // rodančiu į JAU EGZISTUOJANTĮ DOM mazgą (ne į dar nepritvirtintą ref'ą).
    const [captureEventSourceEl, setCaptureEventSourceEl] = useState(null);
    const [openInnerSelectingDialogModal, setOpenInnerSelectingDialogModal] = useState(false);
    const [openOuterSelectingDialogModal, setOpenOuterSelectingDialogModal] = useState(false);
    const [selectedShelf, setSelectedShelf] = useState({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
    useEffect(() => { if (onSelectedShelfChange) onSelectedShelfChange(selectedShelf); }, [selectedShelf, onSelectedShelfChange]);
    const [selectedShelfBoard, setSelectedShelfBoard] = useState(null);
    const [selectedDividerKey, setSelectedDividerKey] = useState(null);
    const [mergeMode, setMergeMode] = useState(false);
    const [collidingIds, setCollidingIds] = useState(new Set());
    // depthOnHover numatytasis TRUE — tas pats "visada rodoma" elgesys, koks visada
    // ir buvo iki šio perjungiklio atsiradimo (vartotojas: "Sukurk mygtuka rodyti/
    // nerodyti baldu gyliu on hover"), kad esami naudotojai nepastebėtų jokio
    // pasikeitimo tol, kol patys neišjungs.
    const [measureMode, setMeasureMode] = useState({ room: false, furniture: true, clearance: true, sectionHeights: false, sectionWidths: false, facadeHeights: false, facadeWidths: false, depthOnHover: true });
    const [showDoors, setShowDoors] = useState(true);
    const collisionClearRef = useRef(null);
    const doorJustApplied = useRef(false);
    // 3D kameros (rotate/pan/zoom) sąveikos sekimas — žr. "ar galima rinkti
    // statistika... stebejima kaip ir kas daroma su 3D vaizdu". OrbitControls
    // 'end' įvykis suveikia VIENĄ kartą už gestą (paleidus pelę/pirštą), o ne
    // kiekvieną kadrą kaip 'change' — bet greitų paeiliui gestų (kelių smulkių
    // pasukimų iš eilės) atveju vis tiek RIBOJAMA bent 2s intervalu tarp įrašų,
    // kad nebūtų užtvindoma DB per vieną ilgesnę "žaidimo su kamera" sesiją.
    const lastCameraTrackRef = useRef(0);
    const trackCameraInteraction = useCallback(() => {
        const now = Date.now();
        if (now - lastCameraTrackRef.current < 2000) return;
        lastCameraTrackRef.current = now;
        trackEvent('custom', { label: '3D kamera pajudinta', meta: { kind: 'camera_interaction' } });
    }, []);
    const handleCollision = useCallback((id1, id2) => {
        setCollidingIds(new Set([id1, id2]));
        if (collisionClearRef.current) clearTimeout(collisionClearRef.current);
        collisionClearRef.current = setTimeout(() => setCollidingIds(new Set()), 450);
    }, []);

    useEffect(() => { if (viewMode === '3D') setIsModelReady(false); }, [viewMode]);
    useEffect(() => {
        if (readOnly) {
            setMeasureMode({ room: false, furniture: false, clearance: false, sectionHeights: false, sectionWidths: false, facadeHeights: false, facadeWidths: false });
        } else if (activeStep === 'Patalpa') {
            setMeasureMode({ room: true, furniture: false, clearance: false, sectionHeights: false, sectionWidths: false });
        } else if (activeStep === 'Vidus') {
            setMeasureMode({ room: false, furniture: true, clearance: true, sectionHeights: true, sectionWidths: false });
        } else if (activeStep === 'Išorė') {
            // "Fasadų aukščiai" rodomas pagal nutylėjimą įėjus į Išorė žingsnį.
            setMeasureMode({ room: false, furniture: true, clearance: true, sectionHeights: false, sectionWidths: false, facadeHeights: true, facadeWidths: false });
        } else {
            setMeasureMode({ room: false, furniture: true, clearance: true, sectionHeights: false, sectionWidths: false });
        }
    }, [activeStep, readOnly]);

    // Delete key — remove selected inner item (drawer/shelf) or clear whole section in Vidus step
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== 'Delete') return;
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            const { furnitureId, cabinetIndex, shelfIndex } = selectedShelf;
            if (activeStep !== 'Vidus' || !shelfIndex?.startsWith('INNER__')) return;
            const itemMatch = shelfIndex.match(/__item(\d+)$/);
            const basePath = shelfIndex.replace(/__item\d+$/, '');
            const optKey = `${furnitureId}-${cabinetIndex}-${basePath}`;
            if (!sectionOptions?.[optKey]) return;
            e.preventDefault();
            if (itemMatch && Array.isArray(sectionOptions[optKey])) {
                // Delete only the specific selected item — same fix as InnerSelectingDialog.jsx's
                // handleRemoveItem: fold the removed item's own vacated space (its gap-above +
                // its height) into whatever now takes its old slot, so the REMAINING items keep
                // their absolute positions instead of the stack sliding up to close the gap (see
                // /admin/bugs "lentynos pakeite pozicija, turejo likti savo senoje pozicijoje" —
                // this keyboard-Delete path had the exact same unpatched bug).
                const itemIdx = parseInt(itemMatch[1], 10);
                const items = sectionOptions[optKey];
                const removed = items[itemIdx];
                const removedSpaceCm = getInnerItemGapAboveCm(removed, itemIdx) + (removed.height || 30);
                const newItems = items.filter((_, i) => i !== itemIdx);
                const next = newItems[itemIdx];
                if (next && INNER_SHELF_TYPES.has(next.type)) {
                    newItems[itemIdx] = { ...next, topGap: (next.topGap ?? INNER_SHELF_TOP_GAP_DEFAULT_CM[next.type]) + removedSpaceCm };
                } else if (next && itemIdx === 0) {
                    newItems[0] = { ...next, topGapCm: (next.topGapCm ?? 0) + removedSpaceCm };
                }
                onSectionOptionSelect(furnitureId, cabinetIndex, basePath, newItems.length > 0 ? newItems : 0);
                setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
            } else {
                // No specific item selected — clear the entire section option
                onSectionOptionSelect(furnitureId, cabinetIndex, basePath, 0);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [activeStep, selectedShelf, sectionOptions, onSectionOptionSelect, setSelectedShelf]);

    // Pasirinktos sekcijos faktiniai matmenys (cm) — naudojami durelių/stalčių ir vidinių
    // komponentų pasirinkimo dialoguose, kad apribotų netinkamo dydžio variantus.
    // shelfIndex čia yra RecursiveSection medžio kelio eilutė ("0", "0-1", arba inner
    // "INNER__<outerPath>__<innerPath>"), todėl tikrasis dydis turi būti paskaičiuotas
    // einant per skaidymo medį (cab.structure / cab.interiorStructure), o ne cab.shelves.
    const selectedSectionDims = useMemo(() => {
        const { furnitureId, cabinetIndex, shelfIndex } = selectedShelf;
        if (cabinetIndex === null || shelfIndex === null) return null;
        // cabinetIndex yra indeksas TOS PAČIOS furnitureId grupės kabinetų sąraše, ne visame `cabinets`.
        const groupCabinets = furnitureId ? cabinets.filter(c => c.furnitureId === furnitureId) : cabinets;
        const cab = groupCabinets[cabinetIndex];
        if (!cab) return null;

        const cabBasisType = Number(cab.basisType) ?? 0;
        const cabBasisHeight = (cabBasisType === 0 || cabBasisType === 5) ? 0 : (Number(cab.basisHeight) || Number(basisHeight) || 0.1);
        const rootWidth = Math.max(0, (Number(cab.width) || 0) - CABINET_WALL_THICKNESS * 2);
        const rootHeight = Math.max(0, (Number(cab.height) || 0) - cabBasisHeight - CABINET_WALL_THICKNESS * 2);

        const baseShelfIndex = shelfIndex.replace(/__item\d+$/, '');
        const innerSplitIdx = baseShelfIndex.indexOf('INNER__');
        let width, height;
        if (innerSplitIdx === -1) {
            ({ width, height } = resolveLeafSize(cab.structure, baseShelfIndex, rootWidth, rootHeight, CABINET_WALL_THICKNESS));
        } else {
            const rest = baseShelfIndex.slice(innerSplitIdx + 'INNER__'.length);
            const sepIdx = rest.indexOf('__');
            const outerPath = sepIdx === -1 ? rest : rest.slice(0, sepIdx);
            const innerPath = sepIdx === -1 ? '0' : rest.slice(sepIdx + 2);
            const outerLeaf = resolveLeafSize(cab.structure, outerPath, rootWidth, rootHeight, CABINET_WALL_THICKNESS);
            const innerNode = (cab.interiorStructure || {})[outerPath] || {};
            ({ width, height } = resolveLeafSize(innerNode, innerPath, outerLeaf.width, outerLeaf.height, CABINET_WALL_THICKNESS));
        }

        return {
            widthCm: Math.max(0, width) * 100,
            heightCm: Math.max(0, height) * 100,
            depthCm: (Number(cab.depth) || 0) * 100,
        };
    }, [cabinets, basisHeight, selectedShelf]);

    const handleSectionOptionSelectInternal = useCallback((optionIndex) => {
        if (selectedShelf.furnitureId !== null && selectedShelf.cabinetIndex !== null && selectedShelf.shelfIndex !== null) {
            const basePath = selectedShelf.shelfIndex.replace(/__item\d+$/, '');
            onSectionOptionSelect(selectedShelf.furnitureId, selectedShelf.cabinetIndex, basePath, optionIndex);
        }
    }, [selectedShelf, onSectionOptionSelect]); // <- PASKEISTA ČIA: onOptionSelect į onSectionOptionSelect

    const handleDividerSelect = useCallback((key) => {
        // Public "Žiūrėti 3D" product viewer: selecting anything opens the same
        // delete/convert/merge overlay buttons the live editor uses — editing
        // only ever belongs on /konfiguratorius, never here.
        if (readOnly) return;
        setSelectedDividerKey(key);
        setSelectedShelfBoard(null);
        if (key !== null) setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
    }, [readOnly]);

    const handleShelfClick = useCallback((furnitureIdParam, cabinetIdx, shelfIdx) => {
        // Same rationale as handleDividerSelect above — this is the single
        // entry point every section/shelf click in SingleCabinet3D.jsx goes
        // through (onShelfClick prop), and it's what makes the "Pašalinti
        // dureles"/"Pasirinkti / keisti dureles"/etc. overlay buttons appear
        // via selectedShelf — block it here rather than trying to gate every
        // downstream overlay button individually.
        if (readOnly) return;
        setSelectedDividerKey(null);
        setSelectedShelfBoard(null);
        if (furnitureIdParam !== activeFurnitureId) onSelect(furnitureIdParam);

        const newSelectedShelf = { furnitureId: furnitureIdParam, cabinetIndex: cabinetIdx, shelfIndex: shelfIdx };

        const isSameShelfClicked = selectedShelf.furnitureId === furnitureIdParam &&
                                   selectedShelf.cabinetIndex === cabinetIdx &&
                                   selectedShelf.shelfIndex === shelfIdx;

        if (activeStep === 'Vidus') {
            // No toggle-to-deselect here (unlike the other steps below): every click on a
            // Vidus section now opens its configuration dialog directly (see
            // VIDUS_ITEM_3D_INTERACTION_DISABLED in SingleCabinet3D.jsx), so clicking the
            // same section a second time in a row must still select it, not clear the
            // selection out from under the dialog that's about to open right after this.
            setOpenInnerSelectingDialogModal(false);
            setSelectedShelf(newSelectedShelf);
        } else if (activeStep === 'Išorė') {
            // ClickUp "3.0 Išorė. Durelių action mygtukai. Sunkiai iššaukiu durelių
            // Action mygtukus" — PATAISYTA (pašalintas toggle-to-deselect, ta pati
            // logika, kaip Vidus aukščiau): hover-žalia ir selected-žalia atrodo
            // VIZUALIAI VIENODAI, tad vartotojas negali atskirti "durelės jau
            // pažymėtos" nuo "tiesiog užvestas pelės kursorius" — paspaudus JAU
            // pažymėtas dureles ANTRĄ kartą (pvz. nepastebėjus, kad mygtukai jau
            // rodėsi, arba tiesiog paspaudus dar kartą), šis toggle jas IŠJUNGDAVO,
            // o žalia spalva likdavo (hover), sukurdama įspūdį, kad "paspaudimas
            // kartais tiesiog neveikia". Kadangi atžymėjimas tuščioje vietoje jau
            // veikia atskirai (žr. handleCanvasMiss/#17), šis toggle nebereikalingas
            // — paspaudimas dabar VISADA pažymi (niekada neišjungia) dureles/sekciją.
            setSelectedShelf(newSelectedShelf);
            setMergeMode(false);
        } else if (isSameShelfClicked) {
            setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
        } else {
            setSelectedShelf(newSelectedShelf);
        }
    }, [selectedShelf, activeStep, onSelect, activeFurnitureId, readOnly]);

    const handleCreateDoors = (cabinetIndex, sectionIndex, targetCabinetIndex, targetSectionIndex, existingGroupId = null) => {
        const fid = activeFurnitureId;
        setMergedSections(prev => {
            const currentMerged = prev || [];
            let groupId = existingGroupId;
            if (!groupId) {
                const group1 = currentMerged.find(s => s.furnitureId === fid && s.cabinetIndex === cabinetIndex && (s.sectionIndex === sectionIndex || s.targetSectionIndex === sectionIndex));
                const group2 = currentMerged.find(s => s.furnitureId === fid && s.cabinetIndex === targetCabinetIndex && (s.sectionIndex === targetSectionIndex || s.targetSectionIndex === targetSectionIndex));
                if (group1) groupId = group1.groupId;
                else if (group2) groupId = group2.groupId;
                else groupId = Date.now();
            }
            const exists = currentMerged.some(s => s.furnitureId === fid && s.cabinetIndex === cabinetIndex && s.sectionIndex === sectionIndex && s.targetCabinetIndex === targetCabinetIndex && s.targetSectionIndex === targetSectionIndex && s.groupId === groupId);
            if (exists) return currentMerged;
            return [...currentMerged, { furnitureId: fid, cabinetIndex, sectionIndex, targetCabinetIndex, targetSectionIndex, groupId }];
        });
    };

    const handleRemoveDoorsInternal = (furnitureId, cabinetIndex, firstSectionIndex, groupId) => {
        const key = `${furnitureId}-${cabinetIndex}-${firstSectionIndex}`;
        if (onDoorRemoval) {
            onDoorRemoval(key, groupId);
        }
    };

    const handleDetachSection = (furnitureId, cabinetIndex, sectionPath, groupId) => {
        setMergedSections(prev => {
            const current = prev || [];
            return current.filter(s =>
                !(s.furnitureId === furnitureId && s.groupId === groupId &&
                  s.cabinetIndex === cabinetIndex && s.targetSectionIndex === sectionPath)
            );
        });
    };

    const groupedCabinets = useMemo(() => {
        const groups = {};
        cabinets.forEach((cab) => {
            // Fall back to this cabinet's own uniqueId (not a shared 'default' string) when
            // furnitureId is missing, so cabinets that lost their furnitureId never get silently
            // merged into the same group as an unrelated cabinet that also lacks one.
            const fid = cab.furnitureId || cab.uniqueId || 'default';
            const initialZ = (typeof cab.z === 'number') ? cab.z : 0;
            if (!groups[fid]) groups[fid] = { id: fid, cabinets: [], x: Number(cab.x) || 0, y: Number(cab.y) || 0, z: initialZ };
            groups[fid].cabinets.push(cab);
        });
        return Object.values(groups);
    }, [cabinets]);

    // Y (aukščio) koordinatė, ties kuria patalpos gylio linija (WallMeasurementsOverlay)
    // turi stovėti, kad sutaptų su aktyvaus baldo PAČIO gylio linijos aukščiu (žr.
    // ClickUp "2.0 Baldas. Gylio matmenų vieta" ir FurnitureGroup.jsx komentarą prie
    // floorY). TIK Baldas/Išorė/Vidus žingsniuose — baldas tada realiai piešiamas ir
    // matomas kartu su patalpa (žr. `activeStep !== 'Patalpa' && groupedCabinets.map`
    // žemiau, ta pati sąlyga). "Patalpa" žingsnyje FIKSUOTAI lieka 0 (senas, prieš šį
    // pakeitimą buvęs elgesys) — grįžus į šį žingsnį PO to, kai baldas jau sukurtas,
    // sinchronizuojant su tada jau nebereikšmingu (šiame žingsnyje nematomo) baldo
    // laisvumu, linija netikėtai pasislinkdavo ir "užlipdavo" ant grindų brėžinio (žr.
    // /admin/bugs "Measurements dabar užlipa ant baldo 3D vazdo, padaryk kaip buvo
    // anksciau" — pataisyta apribojant sinchronizaciją tik tiems žingsniams, kur ji
    // prasminga).
    // Tik PATI baldo Y pozicija (žalia reikšmė, be jokios paraštės) — WallMeasurementsOverlay
    // pati (viduje, turėdama gyvą kameros/PIXEL_OFFSET_M prieigą, žr. ten) pridės tinkamą,
    // dinamišką paraštę. Anksčiau čia buvo skaičiuojama PILNA (su paraštе) Y reikšmė
    // naudojant FIKSUOTAS "-0.06"/"-0.2" konstantas — jos buvo sutapdintos su baldo paties
    // gylio linijos SENU (~0.12m) postūmiu; padidinus tą postūmį (žr.
    // [[feedback_camera_fit_contain_vs_cover]] naujausius punktus), šios konstantos liko
    // pasenusios ir "Patalpos gylis" ženkliukas atsidūrė AUKŠČIAU (ne žemiau, kaip turėtų)
    // už "Baldo gylis" — vartotojas: "sukeisk baldo gylio ir patalpos gylio matmenys
    // vietomis". Dabar paduodamas GRYNAS Y, o paraštė skaičiuojama VIENĄ kartą, gyvai,
    // ten, kur jau yra PIXEL_OFFSET_M.
    const activeFurnitureY = useMemo(() => {
        if (activeStep === 'Patalpa') return 0;
        const activeGroup = groupedCabinets.find(g => g.id === activeFurnitureId) || groupedCabinets[0];
        return activeGroup ? (Number(activeGroup.y) || 0) : 0;
    }, [groupedCabinets, activeFurnitureId, activeStep]);
    // CameraAligner (Canvas vaikas, turi savo camera/size per useThree()) dabar PATS
    // skaičiuoja tikslią paraštę iš activeFurnitureY — žr. ten preciseDepthLineY,
    // dubliuojančią šią pačią WallMeasurementsOverlay formulę. Anksčiau čia buvo
    // skaičiuojama apytikslė, FIKSUOTA "-0.35" paraštė ir paduodama CameraAligner —
    // bet ji buvo per maža (trūko ~11.7cm), kas mažame lange nukirpdavo ženkliuką.

    const handleCanvasMiss = (e) => {
        if (onDeselectAll) onDeselectAll();
        setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
        // ClickUp 3.0 "Sunku nuimti pertvarų/lentynų pažymėjimus" (2026-10-26):
        // ši funkcija anksčiau valė TIK selectedShelf — selectedDividerKey ir
        // selectedShelfBoard (pertvaros/lentynos pažymėjimas) liko pažymėti net
        // paspaudus tuščią (nieko neatitinkantį) tašką. Dabar visi trys valomi
        // kartu — žr. tą patį pataisymą FurnitureGroup.jsx catch-all onClick,
        // kuris dengia atvejį, kai paspausta NE tuščia vieta, o KITA baldo dalis.
        setSelectedDividerKey(null);
        setSelectedShelfBoard(null);
        onObstacleSelect(null);
        // onSplitSection(null); // Išeiti iš skaidymo režimo, jei paspausta kažkur kitur - šis valdomas InnerSelectingDialog uždaryme arba step'o pakeitime.
    };

    // ClickUp 3.0 "Sunku nuimti pertvarų/lentynų pažymėjimus" (2026-10-26):
    // r3f onPointerMissed (handleCanvasMiss aukščiau) suveikia TIK paspaudus
    // VISIŠKAI tuščią vietą — bet kokia kita objekto geometrija (kita baldo
    // dalis, kurios paspaudimo tvarkyklė nebuvo specialiai pritaikyta šiam
    // pažymėjimui) tą paspaudimą "sugauna" pirma (raycast intersection), tad
    // onPointerMissed niekada nepasiekiamas. Šis callback'as perduodamas
    // FurnitureGroup.jsx viršutiniam "catch-all" onClick (ten, kur jau
    // sustabdyta sklaida iš VISŲ konkrečiai apdorotų paspaudimo taikinių —
    // žr. e.stopPropagation() kiekvienoje onShelfClick/onDividerSelect
    // vietoje SingleCabinet3D.jsx), tad čia saugu besąlygiškai išvalyti —
    // jei pasiekė šitą tašką, tai TIKRAI NE pažymėtos pertvaros/lentynos
    // paspaudimas.
    const handleClearShelfSelections = useCallback(() => {
        setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
        setSelectedDividerKey(null);
        setSelectedShelfBoard(null);
    }, []);

    const wallMeasurementsDisabled = activeStep !== 'Patalpa';
    // Rodyklių/matmenų grupė (plotis/aukštis/gylis) turi sekti TĄ PATĮ kraštą, prie kurio
    // dabar fiksuota kamera (žr. CameraAligner zBias komentarą aukščiau) — pagal vartotojo
    // patikslinimą tai yra ATVIROJI/artimiausia kamerai pusė (auganti kartu su gyliu), NE
    // Galinė siena (fiksuota Z≈0, žr. Wall.jsx). Ankstesnis bandymas ("dabar rodyklių grupė
    // pririšta prie TO PATIES fiksuoto Galinės sienos paviršiaus, Z≈0") buvo pagrįstas
    // KLAIDINGU "priekis = Galinė siena" supratimu — kai kamera persiorientavo į teisingą
    // pusę, ši Z≈0.01 grupė (kartu su ja susijusi Galinė siena) ėmė vizualiai trauktis/
    // mažėti tolyn nuo kameros kartu su gyliu, tad rodyklės "užlipdavo" ant besitraukiančio
    // 3D vaizdo, nebeatitikdamos savo linijų realaus dydžio kadre (žr. vartotojo pranešimą
    // "Measurements neturėtų judėti... dabar 'lipa' ant 3D patalpos vaizdo").
    // PATAISYTA: buvęs `wallDepth/100+0.01` naudojo TIK kairės sienos gylį — asimetrinėje
    // Nišoje (kairė seklesnė už dešinę, pvz. 400/600) matmenų linijos/ženkliukai likdavo
    // prie SEKLESNĖS (kairės) sienos Z, o GRINDYS (Wall.jsx, teisingai) tęsėsi iki
    // GILESNĖS (dešinės) — rodyklių grupė atsidurdavo giliai VEWIDUJE grindų ploto, ne prie
    // jų priekinio krašto, tad apatinė (pločio) linija/ženkliukas vizualiai "įlįsdavo" į
    // grindis (žr. vartotojo screenshot). getEffWallDepth (bendra su CameraAligner/
    // HumanSilhouette, žr. aukščiau) grąžina TIKRĄJĮ, rodomos geometrijos priekį.
    const measurementsZ = getEffWallDepth(roomShape, wallDepth, wallDepthRight) / 100 + 0.01;

    // Bendras "Rodyti gylius" (PanelDepthTooltip, žr. edgeHover.jsx DepthHoverContext)
    // perjungiklis pridėtas prie VISŲ TRIJŲ žingsnio-mygtukų grupių — šis tooltip'as
    // vienodai veikia Baldas/Vidus/Išorė žingsniuose, tad ir perjungiklis turi likti
    // pasiekiamas visur, kur jie rodomi, ne tik viename konkrečiame žingsnyje.
    const DEPTH_HOVER_BTN = { key: 'depthOnHover', label: 'Gyliai užvedus pelę', bg: '#ffffff', color: '#374151', border: '1px solid #e5e7eb' };

    const MEASURE_BTNS = [
        { key: 'room',      label: 'Patalpos matmenys',  bg: '#d1d5db', color: '#374151', border: 'none' },
        { key: 'furniture', label: 'Baldo matmenys',     bg: '#111827', color: '#ffffff',  border: 'none' },
        { key: 'clearance', label: 'Laisvumo tarpeliai', bg: '#ffffff', color: '#374151',  border: '1px solid #e5e7eb' },
        DEPTH_HOVER_BTN,
    ];

    const VIDUS_BTNS = [
        { key: 'sectionHeights', label: 'Skyrių aukščiai', bg: '#111827', color: '#ffffff', border: 'none' },
        { key: 'sectionWidths',  label: 'Skyrių pločiai',  bg: '#ffffff', color: '#374151', border: '1px solid #e5e7eb' },
        DEPTH_HOVER_BTN,
    ];

    const ISHORE_BTNS = [
        { key: 'facadeHeights', label: 'Fasadų aukščiai', bg: '#111827', color: '#ffffff', border: 'none' },
        { key: 'facadeWidths',  label: 'Fasadų pločiai',  bg: '#ffffff', color: '#374151', border: '1px solid #e5e7eb' },
        DEPTH_HOVER_BTN,
    ];

    return (
        <DepthHoverContext.Provider value={measureMode.depthOnHover}>
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            {viewMode === '3D' ? (
                <>
                    {!isModelReady && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', zIndex: 10 }}><Spinner /></div>}

                    {/* Patalpos matmenys toggle — shown in Patalpa step */}
                    {activeStep === 'Patalpa' && (
                        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 20, pointerEvents: 'auto' }}>
                            <ToggleButton
                                appearance="subtle"
                                checked={measureMode.room}
                                onClick={() => setMeasureMode(prev => ({ ...prev, room: !prev.room }))}
                                icon={measureMode.room
                                    ? <VisibilityIcon style={{ fontSize: 18 }} />
                                    : <VisibilityOffIcon style={{ fontSize: 18 }} />}
                                style={{
                                    background: '#d1d5db', color: '#374151',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                    fontSize: 11, whiteSpace: 'nowrap',
                                }}
                            >
                                Patalpos matmenys
                            </ToggleButton>
                        </div>
                    )}

                    {/* Measurement mode toggle — shown in Baldas step always */}
                    {!readOnly && activeStep === 'Baldas' && (
                        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'auto' }}>
                            {MEASURE_BTNS.map(btn => (
                                <ToggleButton
                                    key={btn.key}
                                    appearance="subtle"
                                    checked={measureMode[btn.key]}
                                    onClick={() => setMeasureMode(prev => ({ ...prev, [btn.key]: !prev[btn.key] }))}
                                    icon={measureMode[btn.key]
                                        ? <VisibilityIcon style={{ fontSize: 16 }} />
                                        : <VisibilityOffIcon style={{ fontSize: 16 }} />}
                                    style={{
                                        background: btn.bg, color: btn.color, border: btn.border,
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                        fontSize: 11, whiteSpace: 'nowrap',
                                    }}
                                >
                                    {btn.label}
                                </ToggleButton>
                            ))}
                        </div>
                    )}

                    {/* Skyrių aukščiai/pločiai toggle — shown in Vidus step always; mutually exclusive */}
                    {!readOnly && activeStep === 'Vidus' && (
                        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'auto' }}>
                            {VIDUS_BTNS.map(btn => (
                                <ToggleButton
                                    key={btn.key}
                                    appearance="subtle"
                                    checked={measureMode[btn.key]}
                                    onClick={() => setMeasureMode(prev => (btn.key === 'depthOnHover'
                                        ? { ...prev, depthOnHover: !prev.depthOnHover }
                                        : {
                                            ...prev,
                                            sectionHeights: btn.key === 'sectionHeights' ? !prev.sectionHeights : false,
                                            sectionWidths: btn.key === 'sectionWidths' ? !prev.sectionWidths : false,
                                        }))}
                                    icon={measureMode[btn.key]
                                        ? <VisibilityIcon style={{ fontSize: 16 }} />
                                        : <VisibilityOffIcon style={{ fontSize: 16 }} />}
                                    style={{
                                        background: btn.bg, color: btn.color, border: btn.border,
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                        fontSize: 11, whiteSpace: 'nowrap',
                                    }}
                                >
                                    {btn.label}
                                </ToggleButton>
                            ))}
                        </div>
                    )}

                    {/* Rodyti dureles / kontūrus toggle — shown in Išorė step always */}
                    {!readOnly && activeStep === 'Išorė' && (
                        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 20, pointerEvents: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {[
                                { key: 'doors', state: showDoors, toggle: () => setShowDoors(v => !v), label: 'Rodyti dureles' },
                                { key: 'edges', state: showDoorEdges, toggle: () => setShowDoorEdges(v => !v), label: 'Rodyti kontūrus' },
                            ].map(({ key, state, toggle, label }) => (
                                <ToggleButton
                                    key={key}
                                    appearance="subtle"
                                    checked={state}
                                    onClick={() => { trackEvent('custom', { label: `${label} → ${!state}`, meta: { kind: 'toggle', key } }); toggle(); }}
                                    icon={state
                                        ? <VisibilityIcon style={{ fontSize: 16 }} />
                                        : <VisibilityOffIcon style={{ fontSize: 16 }} />}
                                    style={{
                                        background: '#d1d5db', color: '#374151',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                        fontSize: 11, whiteSpace: 'nowrap',
                                    }}
                                >
                                    {label}
                                </ToggleButton>
                            ))}
                            {ISHORE_BTNS.map(btn => (
                                <ToggleButton
                                    key={btn.key}
                                    appearance="subtle"
                                    checked={measureMode[btn.key]}
                                    onClick={() => setMeasureMode(prev => (btn.key === 'depthOnHover'
                                        ? { ...prev, depthOnHover: !prev.depthOnHover }
                                        : {
                                            ...prev,
                                            facadeHeights: btn.key === 'facadeHeights' ? !prev.facadeHeights : false,
                                            facadeWidths: btn.key === 'facadeWidths' ? !prev.facadeWidths : false,
                                        }))}
                                    icon={measureMode[btn.key]
                                        ? <VisibilityIcon style={{ fontSize: 16 }} />
                                        : <VisibilityOffIcon style={{ fontSize: 16 }} />}
                                    style={{
                                        background: btn.bg, color: btn.color, border: btn.border,
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                        fontSize: 11, whiteSpace: 'nowrap',
                                    }}
                                >
                                    {btn.label}
                                </ToggleButton>
                            ))}
                        </div>
                    )}

                    <div ref={onFramesRendered ? setCaptureEventSourceEl : undefined} style={{ opacity: isModelReady ? 1 : 0, transition: 'opacity 0.3s ease-in', width: '100%', height: '100%' }}>
                        {(!onFramesRendered || captureEventSourceEl) && <Canvas
                            style={{ width: '100%', height: '100%' }}
                            // near/far TYČIA nustatyti (anksčiau nebuvo — three.js numatytieji
                            // near=0.1/far=2000, santykis 1:20000) — žr. identišką
                            // directionalLight shadow camera komentarą žemiau (near/far
                            // "shadow acne" pataisymas): TAS PATS gylio (Z-buffer) tikslumo
                            // praradimas, bet čia PAGRINDINIAME vaizde, ne šešėlyje — du
                            // paviršiai su TIKSLIAI sutampančiomis koordinatėmis (patvirtinta
                            // Plokščių koordinatės įrankiu) vizualiai "nesusiliesdavo"/
                            // persišviesdavo per plyšį, nes GPU gylio buferis neturėjo
                            // pakankamai bitų tą tašką vienareikšmiškai išspręsti tokiu
                            // atstumu. far=60 su kaupu pakanka bet kokiam realiam kambariui/
                            // baldui (niekas čia niekad nerodoma už kelių metrų) — žr. porinį
                            // OrbitControls maxDistance žemiau, kad vartotojas negalėtų
                            // atsiritinti toliau už šią ribą (kitaip tolimas planas būtų
                            // apkarpomas/dingtų).
                            camera={{ position: [0, 0, 10], fov: 50, near: 0.1, far: 60 }}
                            // "percentage" (PCFShadowMap) vietoj numatytojo bare `shadows` (PCFSoftShadowMap) —
                            // žr. žemiau esantį directionalLight komentarą: soft variantas prideda SAVO
                            // minkštinimo branduolį virš jau ir taip neryškaus (under-resolved) šešėlio
                            // žemėlapio, dar labiau ištempdamas siaurą šešėlį.
                            shadows="percentage"
                            dpr={[1, 2]}
                            // /admin/bugs "Konfiguratorius ir kompiuterio resursai" — numatytasis
                            // frameloop="always" perpiešia VISĄ sceną kas kadrą (~60/s) net kai
                            // niekas nesikeičia (vartotojas tiesiog žiūri į nejudantį vaizdą),
                            // nuolat apkraudamas GPU. "demand" perpiešia TIK kai kažkas realiai
                            // pasikeitė (OrbitControls/TransformControls — abu savaime kviečia
                            // invalidate() — arba bet koks React prop pasikeitimas scenoje); žr.
                            // CameraAligner/ZoomToFit invalidate() komentarus dėl IMPERATYVIŲ
                            // kameros pozicijos keitimų, kuriems to prireikė atskirai. "always"
                            // paliktas TIK dviem atvejams, kuriems realiai reikia NUOLATINIO
                            // perpiešimo: autoRotate (produkto demonstravimo sukimasis) ir
                            // onFramesRendered (CaptureFrameNotifier skaičiuoja kadrus prieš
                            // fiksuodamas ekrano nuotrauką — "demand" režimu tas skaičiavimas
                            // niekada nepasiektų slenksčio).
                            frameloop={(onFramesRendered || (autoRotate && enableFreeRotate)) ? 'always' : 'demand'}
                            // preserveDrawingBuffer TYČIA paliktas (ne sąlyginis) — Layout.jsx
                            // "Į krepšelį" srautas skaito ŠIO (ne atskiro off-screen) canvas'o
                            // gl.domElement.toDataURL() kelis rAF kadrus VĖLIAU, ne tą pačią
                            // piešimo užduotį, tad be preserveDrawingBuffer nuotrauka būtų tuščia
                            // (naršyklė iki tol jau būtų išvaliusi/sukeitusi buferį).
                            gl={{ preserveDrawingBuffer: true, antialias: true }}
                            eventSource={onFramesRendered ? captureEventSourceEl : undefined}
                            onCreated={({ gl, scene, camera }) => { if (setRendererRef) setRendererRef(gl); if (setSceneRef) setSceneRef(scene); if (setCameraRef) setCameraRef(camera); if (process.env.NODE_ENV !== 'production') window.__r3fDebug = { gl, scene, camera }; }}
                            onPointerMissed={handleCanvasMiss}
                        >
                            <CameraAligner cabinets={cabinets} basisHeight={basisHeight} onReady={setIsModelReady} currentComponentId={currentComponentId} wallWidth={wallWidth} wallHeight={wallHeight} wallDepth={wallDepth} wallDepthRight={wallDepthRight} roomShape={roomShape} activeFurnitureId={activeFurnitureId} activeStep={activeStep} obstacles={obstacles} wallThickness={wallThickness} hideWall={hideWall} measureRoom={measureMode.room} activeFurnitureY={activeFurnitureY} />
                            <ZoomToFit fitTrigger={fitTrigger} cabinets={cabinets} wallWidth={wallWidth} wallHeight={wallHeight} wallDepth={wallDepth} wallThickness={wallThickness} />
                            {onFramesRendered && <CaptureFrameNotifier key={frameKey} onReady={onFramesRendered} minFrames={8} minElapsedMs={1200} />}

                            <color attach="background" args={[hideWall ? '#fafaf9' : (ROOM_STYLE_CONFIGS[roomStyle3D]?.bg || '#fafaf9')]} />
                            {/* hideWall (izoliuotas produkto vaizdas — "Baldas"/"Išorė"/"Vidus" skirtukai,
                                AR/video eksportas) anksčiau turėjo NEPROPORCINGAI stiprų ambient (1.35) šalia
                                silpnesnės kryptinės šviesos — ambient šviečia vienodai iš visų pusių, tad toks
                                aukštas lygis "nuplaudavo" bet kokį roughness/metalness paviršiaus atspindį į
                                plokščią, kontrastą. Kartu su currentColor pilku atspalviu (žr. CabinetPanel/
                                OuterDoors — dabar taisyta) tai ir sukurdavo bendrą "labai pilko" įspūdį.
                                Subalansuota: mažesnis ambient/hemisphere, stipresnė kryptinė šviesa. */}
                            <ambientLight intensity={hideWall ? 0.75 : 0.12} color="#ffffff" />
                            <hemisphereLight intensity={hideWall ? 0.4 : 0.1} groundColor={hideWall ? '#c8c8c8' : '#d8cec0'} color="#ffffff" />
                            {/* shadow-camera-*: anksčiau NIEKADA nenustatyta, tad naudotas Three.js
                                DirectionalLight numatytasis šešėlio kameros frustum'as (apytiksliai
                                left/right/top/bottom=±5, BET near=0.5, far=500 — t.y. 1000:1 gylio
                                diapazonas, suspaustas į TĄ PATĮ 1024×1024 šešėlio žemėlapio gylio
                                tikslumą). Toks platus near/far diapazonas yra klasikinė, gerai žinoma
                                "shadow acne"/savišešėliavimo priežastis TIKSLIAI ten, kur du beveik
                                arba visiškai koplanarūs/besiliečiantys paviršiai susitinka (čia:
                                durelės vs karkaso rėmas, abu TIKSLIAI tame pačiame 60cm gylyje) —
                                nepriklausomai nuo shadow-bias derinimo, nes tai PRECIZIJOS, ne poslinkio
                                problema. Vartotojas: "vis dar tas pats, panagrinek ivairius kitus
                                variantus" — po DVIEJŲ nepavykusių emissive bandymų (karkasui, paskui
                                durelėms) tai kitas, dar nebandytas kelias: sumažinti patį near/far
                                diapazoną iki realiai reikalingo (šviesa ~12 vienetų nuo origin, baldas/
                                kambarys realiai telpa į kelis vienetus nuo jo), gerokai padidinant
                                efektyvų gylio buferio tikslumą BE jokio papildomo medžiagos "pakėlimo".
                                shadow-normalBias (ne vien bias) pridėtas papildomai — jis konkrečiai
                                taikomas statmenai pačiam paviršiaus normalui, dažnai efektyvesnis
                                prieš acne grazing/beveik lygiagrečiuose kampuose nei vien bias. */}
                            {/* shadow-mapSize 1024→2048 + shadows="percentage" (žr. <Canvas> aukščiau) +
                                normalBias 0.01→0.004: near/far ištempimo (aukščiau) pataisyta PRECIZIJA
                                NEIŠSPRENDĖ visos problemos — liko ATSKIRA, iki šiol neliesta ašis:
                                TEKSELIO TANKIS. Frustum'as ±6 (12 vienetų) / 1024 tekselių = ~1.17cm
                                vienam tekseliui — t.y. 1.8cm pločio šoninis skydelis užima vos ~1.5
                                tekselio PAČIAME šešėlio žemėlapyje, nepriklausomai nuo near/far gylio
                                tikslumo. Vartotojas teisingai pastebėjo: "kaip gali būti, kad 1.8cm
                                pločio skydas vos kelių mm atstumu nuo durelių meta tokį didelį šešėlį"
                                — fiziškai neturėtų, bet TOKS neryškus (under-resolved) šešėlio
                                žemėlapis tą siaurą šešėlį neišvengiamai "išteps" per kelis kartus
                                platesnį nei tikra geometrija. Papildomai bazinis "shadows" (be reikšmės)
                                r3f naudoja PCFSoftShadowMap (papildomas minkštinimo branduolys virš
                                paties žemėlapio) — perjungta į "percentage" (PCFShadowMap), kuris
                                neprideda šio papildomo išteptumo. normalBias 0.01 (1cm!) buvo didesnis
                                už pusę paties elemento pločio — sumažintas proporcingai naujai,
                                tankesnei rezoliucijai (žemesnis bias saugus, nes near/far jau tikslus
                                iš ankstesnio pataisymo). */}
                            <directionalLight position={[5, 10, 5]} intensity={hideWall ? 1.05 : 0.75} color="#f8f8ff" castShadow shadow-mapSize={[2048, 2048]} shadow-bias={-0.0001} shadow-normalBias={0.004} shadow-camera-near={1} shadow-camera-far={25} shadow-camera-left={-6} shadow-camera-right={6} shadow-camera-top={6} shadow-camera-bottom={-6} />
                            {/* hideWall 0.45→0.8: vartotojas pastebėjo, kad atviros (be durelių) sekcijos
                                ŠONINĖS sienelės (matomos iš vidaus, status kampu) atrodo pastebimai
                                tamsesnės/asimetriškos — kairė (apšviesta pagrindinės [5,10,5] šviesos
                                +X komponento) ir dešinė (priklauso VIEN nuo ŠITOS, antrinės [-3,5,-2]
                                šviesos -X komponento) buvo apšviečiamos NEVIENODAI, nes 0.45 buvo
                                maždaug perpus silpnesnė už pagrindinės 1.05 — vartotojas: "baldo plokščių
                                ir durelių gylis 60cm, kodėl yra tokie ryškūs nesutapimai ir šešėliai?"
                                (žr. ir atsakymą: tai TIKROS, būtinos korpuso šoninės sienelės, jų
                                patamsėjimas fiziškai pagrįstas kryptinės šviesos kampu — ne bug'as, bet
                                vartotojas pasirinko bendrą papildomą apšvietimą, NE geometrijos keitimą).
                                NEKEIČIAMA: ambient/hemisphere (žr. komentarą aukščiau — tyčia sumažinti
                                praeityje, kad nebūtų "labai pilko" įspūdžio; dar vienas jų padidinimas
                                tą pataisymą atšauktų). Pozicija nekeičiama (tik intensyvumas) — mažesnė
                                rizika nei perkelti patį šviesos šaltinį. Scope: tik hideWall (izoliuotas
                                baldo vaizdas) atvejui — pilno kambario apšvietimas (0.14) nepaliestas. */}
                            <directionalLight position={[-3, 5, -2]} intensity={hideWall ? 0.8 : 0.14} color="#e8ecff" />
                            {/* Papildomo "kambario lempos" taškinio šaltinio čia sąmoningai nebėra —
                                pašalintas, per daug matėsi kaip atskiras šviesos objektas. Bendras
                                patalpos apšvietimas liko sąmoningai pritemdytas (ambient/hemisphere/
                                fill žemiau nei hideWall režime) — ryškesnis kontrastas tarp šviesos
                                ir šešėlio atrodo tikroviškiau nei tolygiai apšviesta studija. */}
                            {/* background={false} — vien apšvietimo/atspindžių šaltinis, jokio matomo fono
                                nepiešia, tad saugu naudoti ir kai hideWall=true (be sienos, švarus fotografavimas) */}
                            <Environment preset="apartment" background={false} intensity={hideWall ? 0.65 : 0.25} />
                            {/* Minkštas kontaktinis šešėlis po baldu — be jo baldas "plūduriuoja"
                                virš grindų ir atrodo dirbtinai, plokščiai. Centruojama apytiksliai
                                pagal sienos plotį/gylį (tikslus baldo centras čia dar nežinomas —
                                jį suskaičiuoja CameraAligner atskirai), tad plotas imamas su kaupu.
                                /admin/bugs "Konfiguratorius ir kompiuterio resursai" — drei's
                                ContactShadows numatytuoju atveju frames=Infinity, t.y. PERPIEŠIA
                                VISĄ (gl.render + 2x blur) šešėlio tekstūrą KAS KADRĄ, amžinai — tai
                                buvo pagrindinis likęs GPU apkrovos šaltinis net nustačius
                                frameloop="demand" aukščiau (jos pačios useFrame nereikalauja NAUJO
                                kadro, bet "pasivažinėja" ant BET KURIO kito kadro, visai
                                nepriklausomai nuo to, ar baldo šešėlis realiai pasikeitė). frames={1}
                                pakanka — kiekvienas kadras čia jau yra PILNAS, nepriklausomas
                                perpiešimas (ne progresyvus kaupimas), tad vieno pakanka tiksliam
                                rezultatui. `key` priverčia tą vieną kadrą PERPIEŠTI iš naujo (React
                                re-mount'ina visą komponentą), kai pasikeičia baldų išdėstymas/
                                skaičius/patalpos matmenys — t.y. kai TIKRAI gali pasikeisti šešėlio
                                forma — nepriklausomai nuo smulkesnių, šešėliui nesvarbių redakcijų
                                (pvz. vidinės lentynos pridėjimo). */}
                            <ContactShadows
                                key={`${cabinets.length}-${cabinets.reduce((s, c) => s + Math.round((Number(c.x) || 0) * 100) + Math.round((Number(c.width) || 0) * 100) + Math.round((Number(c.depth) || 0) * 100), 0)}-${wallWidth}-${wallDepth}`}
                                frames={1}
                                position={[(wallWidth || 600) / 200, -0.005, (wallDepth || 60) / 200]}
                                scale={Math.max(wallWidth || 600, (wallDepth || 60) * 3) / 100 * 1.4}
                                opacity={hideWall ? 0.45 : 0.5}
                                blur={2.2}
                                far={hideWall ? 1.6 : 2.4}
                            />
                            {/* Z pozicija anksčiau buvo (wallDepth||60)/200 — t.y. VISADA tik KAIRĖS
                                sienos gylio pusė, nepriklausomai, kuri siena realiai gilesnė/rodoma.
                                Asimetrinėje Nišoje (pvz. kairė 60cm, dešinė 400cm) tai pastūmė siluetą
                                giliai PATALPOS VIDUJE (toli nuo "priekio"), kur jį kartais pridengdavo
                                kita geometrija (žr. [[feedback_camera_fit_contain_vs_cover]] — siluetas
                                visai nerenderindavosi tam tikrais kampais). Vartotojas: "ar nebutu geriau
                                tada visada ristis prie Z asies priekio... kad jis visada butu priekyje
                                priklausomai kuri siena yra ilgesne" — getEffWallDepth (bendra su
                                CameraAligner, žr. aukščiau) grąžina PILNĄ (ne pusę) tos sienos gylį —
                                siluetas visada stovi lygiai ties patalpos PRIEKIU, ta pati Z, prie
                                kurios fiksuojama ir pati kamera. */}
                            {!hideWall && (
                                <HumanSilhouette position={[(wallWidth || 600) / 100 + 0.65, 0, getEffWallDepth(roomShape, wallDepth, wallDepthRight) / 100]} />
                            )}

                            {/* PASTABA: anksčiau čia OrbitControls buvo visai išjungiamas off-screen
                                fiksavimo egzemplioriams (onFramesRendered), įtariant, kad jo connect()/
                                disconnect() ciklas kaltas dėl "Cannot read properties of null (reading
                                'addEventListener')". Tikroji priežastis buvo r3f PAČIO Canvas'o vidinis
                                events.connect() (žr. eventSource prop žemiau) — OrbitControls su tuo
                                niekuo dėtas. Jis BŪTINAS visada: CameraAligner naudoja useThree().controls,
                                kurį useThree() grąžina TIK jei OrbitControls sumontuotas (makeDefault) —
                                be jo kamera niekada nepersipozicionuoja ir lieka numatytame [0,0,10]
                                atstume, todėl baldas fiksuotoje nuotraukoje atrodydavo mažas. */}
                            {/* presentationOnly (Interactive3DViewer's public product viewer):
                                drag-to-rotate stays allowed (a "spin the product" view is the
                                whole point), but zoom/pan are blocked and it's still auto-rotating
                                — `enabled` must stay TRUE for that, since drei's OrbitControls only
                                calls controls.update() (where autoRotate's spin is applied) when
                                controls.enabled is true. enableZoom/enablePan are three.js's own
                                per-interaction flags, respected independently of the update-loop
                                gate. Hover/emissive-glow feedback on the furniture's own panels and
                                dividers is a SEPARATE concern, not an OrbitControls setting at all
                                — see SingleCabinet3D.jsx's readOnly/hoverActiveStep. */}
                            <OrbitControls
                                makeDefault
                                enabled={orbitEnabled}
                                // /admin/bugs "Konfiguratorius ir kompiuterio resursai" — drei's
                                // OrbitControls defaults enableDamping=true, and its internal
                                // 'change' handler calls invalidate() on EVERY damping tick (see
                                // node_modules/@react-three/drei/core/OrbitControls.js). CameraAligner/
                                // ZoomToFit reposition the camera IMPERATIVELY (camera.position.set,
                                // not through OrbitControls' own rotate/pan/dolly), which desyncs
                                // OrbitControls' internal spherical state from the real camera — with
                                // damping on, it then spends many frames (confirmed live: a sustained
                                // ~30fps, never settling) smoothly "catching up" to that jump, exactly
                                // defeating frameloop="demand" above. Disabled outright: our own
                                // explicit invalidate() calls already provide the single-frame nudge
                                // these imperative jumps need, and enableFreeRotate is false by default
                                // in the main configurator anyway (so there's no drag-rotate inertia to
                                // lose here) — zoom/pan feel marginally less "eased" as the trade-off.
                                enableDamping={false}
                                enableRotate={enableFreeRotate}
                                enableZoom={!presentationOnly}
                                enablePan={!presentationOnly}
                                // Suporuota su <Canvas camera> far=60 aukščiau — be šios ribos
                                // vartotojas galėtų pelės rateliu atsiritinti UŽ far plokštumos,
                                // ir tolimas turinys imtų dingti/apsikarpyti.
                                maxDistance={40}
                                autoRotate={enableFreeRotate && autoRotate}
                                autoRotateSpeed={2}
                                minAzimuthAngle={enableFreeRotate ? -Infinity : -Math.PI / 2.2}
                                maxAzimuthAngle={enableFreeRotate ? Infinity : Math.PI / 2.2}
                                minPolarAngle={enableFreeRotate ? Math.PI / 6 : 0}
                                maxPolarAngle={enableFreeRotate ? Math.PI / 1.4 : Math.PI / 1.8}
                                onEnd={presentationOnly ? undefined : trackCameraInteraction}
                            />

                            {!hideWall && (
                                <Wall
                                    wallWidth={wallWidth}
                                    wallHeight={wallHeight}
                                    wallThickness={wallThickness}
                                    cabinetDepth={cabinets.length ? Math.max(...cabinets.map(c => (Number(c.z) || 0) + (Number(c.depth) || 0))) : 0.5}
                                    wallDepth={wallDepth}
                                    wallDepthRight={wallDepthRight}
                                    currentComponentId={currentComponentId}
                                    roomShape={roomShape}
                                    skirtingType={skirtingType}
                                />
                            )}
                            
                            {measureMode.room && (
                                <WallMeasurementsOverlay
                                    width={wallWidth}
                                    height={wallHeight}
                                    depth={wallDepth}
                                    depthRight={wallDepthRight}
                                    roomShape={roomShape}
                                    onDimensionChange={onDimensionChange}
                                    disabled={wallMeasurementsDisabled}
                                    modalOpen={modalOpen}
                                    displayZ={measurementsZ}
                                    activeFurnitureY={activeFurnitureY}
                                />
                            )}

                            {/* Anksčiau kliūtys buvo rodomos TIK Patalpa žingsnyje — bet jos dažnai
                                yra tikrinamos SANTYKIO su baldo vidumi (ar nesikerta su lentyna ir
                                pan.), tad turi likti matomos ir Baldas/Išorė/Vidus žingsniuose. */}
                            {obstacles && obstacles.map(obs => (
                                <Obstacle3D
                                    key={obs.id}
                                    data={obs}
                                    onEdit={onObstacleEdit}
                                />
                            ))}

                            {activeStep !== 'Patalpa' && groupedCabinets.map((group) => {
                                const groupDepth = group.cabinets.length > 0 ? (group.cabinets.reduce((sum, c) => sum + Number(c.depth || 0), 0) / group.cabinets.length) : 0.6;
                                return (
                                    <FurnitureGroup // Naudojamas FurnitureGroup komponentas
                                        key={group.id}
                                        furnitureId={group.id}
                                        cabinets={group.cabinets}
                                        allCabinets={cabinets} // Perduodami VISI spintos objektai
                                        position={{ x: group.x, y: group.y, z: group.z }}
                                        groupDepth={groupDepth}
                                        isActive={group.id === activeFurnitureId}
                                        readOnly={readOnly}
                                        onSelect={onSelect}
                                        onMove={onMove}
                                        wallWidth={wallWidth}
                                        wallHeight={wallHeight}
                                        wallDepth={wallDepth}
                                        wallDepthRight={wallDepthRight}
                                        roomShape={roomShape}
                                        backWall={backWall}
                                        onCabinetClick={onCabinetClick}
                                        cabinetDepth={group.cabinets[0]?.depth}
                                        currentComponentId={currentComponentId}
                                        selectedShelf={selectedShelf.furnitureId === group.id ? selectedShelf : null} // Teisingas naudojimas
                                        onShelfClick={handleShelfClick}
                                        onClearSelections={handleClearShelfSelections}
                                        mergedSections={mergedSections}
                                        onRemoveDoors={handleRemoveDoorsInternal}
                                        onCreateDoors={handleCreateDoors}
                                        onDetachSection={handleDetachSection}
                                        doorColors={doorColors}
                                        sectionOptions={sectionOptions}
                                        onSectionOptionSelect={onSectionOptionSelect}
                                        sectionComponentOffsets={sectionComponentOffsets}
                                        onSectionComponentOffsetChange={onSectionComponentOffsetChange}
                                        doorHandles={doorHandles}
                                        selectedPlate={selectedPlate}
                                        doorPlates={doorPlates}
                                        activeStep={activeStep}
                                        activeFurnitureId={activeFurnitureId}
                                        onAddShelf={onAddShelf}
                                        onRemoveShelf={onRemoveShelf}
                                        onAddVertical={onAddVertical}
                                        onRemoveVertical={onRemoveVertical}
                                        onDimensionChange={onDimensionChange}
                                        selectedCabinetIndex={selectedCabinetIndex}
                                        allFurnitureGroupRects={allFurnitureGroupRects}
                                        onShelfHeightChange={onShelfHeightChange}
                                        coverPlates={coverPlates}
                                        modalOpen={modalOpen}
                                        onDoorSelection={onDoorSelection}
                                        onDoorRemoval={onDoorRemoval}
                                        onCabinetWidthChange={onCabinetWidthChange}
                                        onInterCabinetResize={onInterCabinetResize}
                                        onCabinetHeightChange={onCabinetHeightChange}
                                        // No fallback to the room-wide leftSideElement/rightSideElement props here —
                                        // that field is shared across every furniture piece, so using it as a
                                        // fallback would leak the last-set value onto every other furniture group.
                                        leftSideElement={group.cabinets[0]?.leftSideElement ?? { type: 0, width: 0 }}
                                        rightSideElement={group.cabinets[0]?.rightSideElement ?? { type: 0, width: 0 }}
                                        topPanelType={group.cabinets[0]?.topPanelType ?? 0}
                                        topPanelHeight={group.cabinets[0]?.topPanelHeight ?? 0.1}
                                        onSplitSection={onSplitSection}
                                        splitSectionMode={splitSectionMode}
                                        onHorizontalSplit={onHorizontalSplit}
                                        onVerticalSplit={onVerticalSplit}
                                        onSectionRatioChange={onSectionRatioChange}
                                        onSectionDelete={onSectionDelete}
                                        onLeafSectionDelete={onLeafSectionDelete}
                                        onNarySectionResize={onNarySectionResize}
                                        onShelfDelete={onShelfDelete}
                                        measureMode={measureMode}
                                        onOpenOuterDialog={() => setOpenOuterSelectingDialogModal(true)}
                                        onOpenInnerDialog={() => { if (activeStep === 'Vidus') setOpenInnerSelectingDialogModal(true); }}
                                        mergeMode={mergeMode}
                                        onToggleMergeMode={() => setMergeMode(v => !v)}
                                        snapshotMode={hideWall}
                                        furnitureType={furnitureType}
                                        slidingDoorCount={slidingDoorCount}
                                        slidingDoorProfile={slidingDoorProfile}
                                        onCollision={(otherId) => handleCollision(group.id, otherId)}
                                        isColliding={collidingIds.has(group.id)}
                                        selectedShelfBoard={selectedShelfBoard}
                                        onSetSelectedShelfBoard={(data) => {
                                            setSelectedShelfBoard(data);
                                            if (data !== null) {
                                                setSelectedDividerKey(null);
                                                setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
                                            }
                                        }}
                                        selectedDividerKey={selectedDividerKey}
                                        onDividerSelect={handleDividerSelect}
                                        onInteriorHSplit={onInteriorHSplit}
                                        onInteriorVSplit={onInteriorVSplit}
                                        onInteriorSectionResize={onInteriorSectionResize}
                                        onInteriorSectionDelete={onInteriorSectionDelete}
                                        onInteriorLeafSectionDelete={onInteriorLeafSectionDelete}
                                        onInteriorNarySectionResize={onInteriorNarySectionResize}
                                        onPromoteAtPanel={onPromoteAtPanel}
                                        onDemoteAtPanel={onDemoteAtPanel}
                                        panelFrontOffsets={panelFrontOffsets}
                                        onTogglePanelFront={onTogglePanelFront}
                                        doorGroupLabels={doorGroupLabels}
                                        onSectionOptionSelect={onSectionOptionSelect}
                                        showDoors={showDoors}
                                        showDoorEdges={showDoorEdges}
                                    />
                                );
                            })}
                        </Canvas>}
                    </div>
                </>
            ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text size={500} style={{ color: tokens.colorNeutralForeground2 }}>Pasirinkite 3D vaizdą, kad pamatytumėte dizainą.</Text>
                </div>
            )}
            <InnerSelectingDialog
                open={openInnerSelectingDialogModal}
                onClose={() => {
                    setOpenInnerSelectingDialogModal(false);
                    if (splitSectionMode) onSplitSection(null); // Išeiti iš skaidymo režimo, jei uždaromas dialogas
                    if (activeStep !== 'Vidus') {
                        setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
                    }
                }}
                selectedSection={selectedShelf}
                sectionOptions={sectionOptions}
                onOptionSelect={handleSectionOptionSelectInternal}
                onSplitSection={onSplitSection} // Perduodame toliau
                sectionDims={selectedSectionDims}
            />

            <OuterSelectingDialog
                open={openOuterSelectingDialogModal}
                onClose={() => {
                    setOpenOuterSelectingDialogModal(false);
                    if (!doorJustApplied.current && activeStep !== 'Vidus') {
                        setSelectedShelf({ furnitureId: null, cabinetIndex: null, shelfIndex: null });
                    }
                    doorJustApplied.current = false;
                }}
                selectedShelfInfo={selectedShelf}
                doorColors={doorColors}
                sectionDims={selectedSectionDims}
                onSelectOption={(opt) => {
                    if (selectedShelf.furnitureId !== null && selectedShelf.cabinetIndex !== null && selectedShelf.shelfIndex !== null) {
                        const key = `${selectedShelf.furnitureId}-${selectedShelf.cabinetIndex}-${selectedShelf.shelfIndex}`;

                        if (opt === 0) {
                            const existingMergedEntry = mergedSections.find(s =>
                                s.furnitureId === selectedShelf.furnitureId && s.cabinetIndex === selectedShelf.cabinetIndex &&
                                (s.sectionIndex === selectedShelf.shelfIndex || s.targetSectionIndex === selectedShelf.shelfIndex)
                            );
                            if (existingMergedEntry) {
                                handleRemoveDoorsInternal(selectedShelf.furnitureId, selectedShelf.cabinetIndex, selectedShelf.shelfIndex, existingMergedEntry.groupId);
                            } else if (onDoorRemoval) {
                                onDoorRemoval(key, null);
                            }
                        } else {
                            doorJustApplied.current = true;
                            if (onDoorSelection) {
                                onDoorSelection(key, opt, selectedShelf.cabinetIndex, selectedShelf.shelfIndex);
                            }
                        }
                    }
                }}
            />
        </div>
        </DepthHoverContext.Provider>
    );
};

export default Builder3D;
